/* eslint-disable max-lines */
/*
 * Bulk Update (Work Queues -> Bulk). One file on purpose: fewer new files in the
 * diff. It is over the repo's 450-line cap, hence the disable above -- read it by
 * the section banners below, they run leaf-first:
 *
 *   Icons -> Portal -> Fields -> FilterRow -> ApplyBar -> Table -> Chooser ->
 *   ConfirmDialog -> Dialog -> Button (the only export the outside world uses)
 *
 * The markup and class names are the approved prototype's, verbatim; the styling
 * lives in ../styles/bulkOverride.css. State lives in ../hooks/useBulkOverride.
 */

import { Button, Tooltip } from '@mui/material';
import { themeQuartz } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { find, isEmpty, map, uniqBy, upperFirst } from 'lodash';
import PropTypes from 'prop-types';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';

import { bulkOverrideOperation } from 'api/requests';
import useLabelRegenerationProgress from 'common/hooks/useLabelRegenerationProgress';
import useNdcOverrideProgress from 'common/hooks/useNdcOverrideProgress';
import usePrintLabelContext from 'common/hooks/usePrintLabelContext';
import useRoles from 'common/hooks/useRoles';
import { getPartners } from 'store/thunks/partnerThunks';

import { CATALOG_DISABLED_LABEL } from 'common/constants/catalogStatus';
import { FILL_LATER_LEAD_TIME_DAYS } from 'common/constants/fillLater';
import {
  BULK_OVERRIDE_ACTIONS,
  BULK_OVERRIDE_APPLY_NOTE,
  BULK_OVERRIDE_HOWTO,
  BULK_OVERRIDE_IDLE_HOWTO,
  BULK_OVERRIDE_IDLE_WARNING,
  BULK_OVERRIDE_REVIEW_REASONS,
  BULK_OVERRIDE_STANDING_FILTER_LABEL,
  FILL_LATER_MODE,
  FILL_LATER_MODES,
  fillLaterModeFlags,
  findBulkOverrideAction,
} from '../constants/bulkOverride';
import useBulkOverride, { MIN_DRUG_SEARCH_LENGTH } from '../hooks/useBulkOverride';
import useLabelRegenPollFallback from '../hooks/useLabelRegenPollFallback';
import { ndcDigits, soleCurrentNdc } from '../utils/bulkOverrideFilters';
import { EMPTY_ROW_EDIT, hasStagedValue, isStagedQuantity } from '../utils/bulkOverrideStaging';
import {
  completeLines,
  formatScheduleDay,
  isRealCalendarDay,
  relativeScheduleDay,
  retainedAfterSchedule,
  rowScheduleProblem,
  scheduleDates,
  scheduleTotal,
} from '../utils/fillLaterSchedule';
import FillLaterScheduleFields, { FillLaterMoveDateField } from './FillLaterScheduleFields';
import '../styles/bulkOverride.css';

/* Row height for a stacked Fill dates cell: the cell's own vertical padding plus one line each. */
const SCHEDULE_LINE_HEIGHT = 18;
const SCHEDULE_ROW_PADDING = 16;

/* The "Staged" chips bucket rows by what was staged on them, so the bucket key MUST be a string.
 * Review stages a scalar reason and buckets on itself, but quantity stages a {ndc, quantity} and
 * the split stages a {splits: [...]} — a fresh object per row, so bucketing on the value gave one
 * chip per row, every one of them keyed "[object Object]", which React reports as duplicate
 * children. The split's key doubles as its label: one signature per distinct schedule. */
const scheduleSignature = (schedule) =>
  completeLines(schedule)
    .map((line) => `${line.quantity} QTY on ${formatScheduleDay(line.scheduledFor)}`)
    .join(' · ');

const stagedBucketKey = (params) => {
  const { staged, isQuantityChange, isFillLaterSplit, isFillLaterMove, isProductChange } = params;

  if (isQuantityChange) return String(staged?.quantity ?? '');
  if (isFillLaterMove) return formatScheduleDay(staged?.scheduledFor);
  if (isFillLaterSplit) return scheduleSignature(staged?.splits);
  /* The NDC is the point of the operation, and it is what the chip shows. The quantity rides
     along on the same staged object but does not distinguish one bucket from another. */
  if (isProductChange) return String(staged?.ndc ?? '');

  /* NDC and review stage a scalar and bucket on themselves. The guard is for the next operation
     that stages an object and forgets a branch here: String(object) is "[object Object]", which
     collapses every row into one unusable key and makes React report duplicate children. */
  return typeof staged === 'object' && staged !== null ? JSON.stringify(staged) : staged;
};

const stagedBucketLabel = (params) => {
  const { reason, isQuantityChange, isFillLaterSplit, isFillLaterMove, isProductChange } = params;

  if (isQuantityChange) return `Qty ${reason}`;
  if (isFillLaterMove) return `Fill on ${reason}`;
  if (isFillLaterSplit || isProductChange) return reason;

  return BULK_OVERRIDE_REVIEW_REASONS[reason];
};

/* ---------- BulkOverrideIcons : shared glyphs and the **bold** renderer ---------- */

/* Every glyph below is copied from the approved prototype. Do not swap them for
 * @mui/icons-material -- the sizes and stroke weights are part of the design. */

const Glyph = ({ size, box, stroke, fill, className, children }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox={`0 0 ${box} ${box}`}
    fill={fill}
    stroke={stroke ? 'currentColor' : 'none'}
    strokeWidth={stroke || undefined}
  >
    {children}
  </svg>
);

Glyph.propTypes = {
  size: PropTypes.number.isRequired,
  box: PropTypes.number.isRequired,
  stroke: PropTypes.number,
  fill: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
};

Glyph.defaultProps = { stroke: 0, fill: 'none', className: undefined };

const X = 'M3.5 3.5l9 9M12.5 3.5l-9 9';
const INFO = 'M8 5.2v.1M8 7.3v3.6';

export const CloseIcon = () => (
  <Glyph size={15} box={16} stroke={1.8}>
    <path d={X} />
  </Glyph>
);

export const ClearIcon = () => (
  <Glyph size={13} box={16} stroke={1.9}>
    <path d={X} />
  </Glyph>
);

export const ClearDrugIcon = () => (
  <Glyph size={14} box={16} stroke={1.9}>
    <path d={X} />
  </Glyph>
);

export const GridIcon = () => (
  <Glyph size={15} box={16} fill='currentColor'>
    {[2, 6.7, 11.4].map((y) =>
      [2, 6.7, 11.4].map((x) => (
        <rect x={x} y={y} width='2.6' height='2.6' rx='.7' key={`${x}-${y}`} />
      ))
    )}
  </Glyph>
);

const BarcodeIcon = () => (
  <Glyph size={19} box={20} stroke={1.6}>
    <path d='M3 4.5v11M6 4.5v11M9 4.5v11M12.5 4.5v11M16 4.5v11' />
  </Glyph>
);

const SwapIcon = () => (
  <Glyph size={19} box={20} stroke={1.6}>
    <rect x='2.6' y='6' width='7' height='7' rx='1.2' />
    <path d='M12 4.6h5.4M17.4 4.6L15.2 2.6M17.4 4.6L15.2 6.8M12.4 14.4h5' />
    <rect x='12' y='9.4' width='5.4' height='1.2' rx='.6' />
  </Glyph>
);

const FlagIcon = () => (
  <Glyph size={19} box={20} stroke={1.6}>
    <path d='M4 3.2h12v13.6l-6-3-6 3z' />
    <path d='M7.4 8.2l2 2 3.2-3.4' />
  </Glyph>
);

export const ClipboardIcon = () => (
  <Glyph size={26} box={24} stroke={1.6}>
    <rect x='5' y='3.4' width='14' height='17.2' rx='2.2' />
    <path d='M9 3.4h6M8.6 9.4h6.8M8.6 13h6.8M8.6 16.6h4' />
  </Glyph>
);

const UndoIcon = () => (
  <Glyph size={19} box={20} stroke={1.6}>
    <path d='M3.4 9.4a7 7 0 1 1 2 5' />
    <path d='M3.2 4.6v5h5' />
  </Glyph>
);

export const ACTION_ICONS = {
  barcode: BarcodeIcon,
  swap: SwapIcon,
  flag: FlagIcon,
  undo: UndoIcon,
};

export const WarnIcon = () => (
  <Glyph size={14} box={16} stroke={1.7}>
    <path d='M8 1.9l6.1 11.7H1.9z' />
    <path d='M8 6.2v3.1M8 11.2v.1' />
  </Glyph>
);

export const InfoIcon = () => (
  <Glyph size={14} box={16} stroke={1.7}>
    <circle cx='8' cy='8' r='6.3' />
    <path d={INFO} />
  </Glyph>
);

export const HintIcon = () => (
  <Glyph size={13} box={16} stroke={1.6}>
    <circle cx='8' cy='8' r='6.3' />
    <path d={INFO} />
  </Glyph>
);

export const ReviewHeadIcon = () => (
  <Glyph size={17} box={20} stroke={1.7}>
    <path d='M3.2 4.2h13.6M5.8 10h8.4M8.6 15.8h2.8' />
  </Glyph>
);

export const BackIcon = () => (
  <Glyph size={14} box={14} stroke={1.8}>
    <path d='M12 7H3M6.5 3.5L3 7l3.5 3.5' />
  </Glyph>
);

export const LockIcon = () => (
  <Glyph size={11} box={12} stroke={1.7}>
    <rect x='2.2' y='5.2' width='7.6' height='5.2' rx='1' />
    <path d='M4 5.2V3.9a2 2 0 0 1 4 0v1.3' />
  </Glyph>
);

export const ChevronIcon = () => (
  <Glyph size={11} box={12} stroke={1.7} className='cv'>
    <path d='M2.5 4.5L6 8l3.5-3.5' />
  </Glyph>
);

/* the head tick is recoloured to navy by `#tm thead .cb svg`; body ticks stay white */
export const TickIcon = () => (
  <svg width='9' height='9' viewBox='0 0 12 12' fill='none' stroke='#fff' strokeWidth='2.4'>
    <path d='M2 6.2l2.6 2.6L10 3.4' />
  </svg>
);

export const DashIcon = () => (
  <svg width='9' height='9' viewBox='0 0 12 12' fill='none' stroke='#fff' strokeWidth='2.6'>
    <path d='M2.6 6h6.8' />
  </svg>
);

export const PrevIcon = () => (
  <Glyph size={11} box={14} stroke={2}>
    <path d='M8.6 2.6L4.2 7l4.4 4.4' />
  </Glyph>
);

export const NextIcon = () => (
  <Glyph size={11} box={14} stroke={2}>
    <path d='M5.4 2.6L9.8 7l-4.4 4.4' />
  </Glyph>
);

export const ConfirmWarnIcon = () => (
  <Glyph size={19} box={20} stroke={1.8}>
    <path d='M10 2.6l7.2 13.8H2.8z' />
    <path d='M10 7.8v3.8M10 13.8v.1' />
  </Glyph>
);

/* Body copy stores emphasis as **markers** so the strings stay plain data in the
 * constants file -- no dangerouslySetInnerHTML needed to render them. */
export const renderMarks = (text) =>
  String(text)
    .split('**')
    .map((part, index) =>
      // eslint-disable-next-line react/no-array-index-key
      index % 2 ? <b key={`b${index}`}>{part}</b> : part
    );

/* ---------- BulkOverridePortal : portal + .bo-root scope + scroll lock ---------- */

/* The prototype's overlays are position:fixed, so they go to document.body --
 * a transformed ancestor anywhere in the page would otherwise become their
 * containing block. .bo-root is what scopes the ported stylesheet.
 *
 * The prototype page set body{overflow:hidden}; locking scroll here does the
 * same, which also keeps .modal's 100vw/100vh exact -- with the page
 * scrollbars still showing, the modal overflows the veil by their width. */
const BulkOverridePortal = ({ children }) => {
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  return createPortal(<div className='bo-root'>{children}</div>, document.body);
};

BulkOverridePortal.propTypes = {
  children: PropTypes.node.isRequired,
};

/* ---------- BulkOverrideFields : form controls: select, text, drug combobox, date range ---------- */

/* The prototype's outlined field: a notched label that floats, an optional
 * clear button, and .on once the field carries a value. */

export const SelectField = (props) => {
  const { label, value, options, width, isMono, onChange } = props;
  const isSet = Boolean(value);

  return (
    <div className={`nf float sel${isSet ? ' on' : ''}`} style={{ width }}>
      <span className='mini'>{label}</span>
      <button
        type='button'
        className='dx'
        onClick={() => onChange('')}
        aria-label={`Clear ${label.toLowerCase()}`}
      >
        <ClearIcon />
      </button>
      <select
        className={isMono ? 'mono' : undefined}
        aria-label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value=''>All</option>
        {options.map((option) => (
          <option value={option.value} key={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

SelectField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  width: PropTypes.number,
  isMono: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

SelectField.defaultProps = {
  value: '',
  options: [],
  width: 196,
  isMono: false,
};

export const TextField = (props) => {
  const {
    label,
    value,
    width,
    isNumeric,
    isInvalid,
    isDisabled,
    isAlwaysFloating,
    onChange,
    onCommit,
  } = props;

  /* isAlwaysFloating keeps the notched label and the solid border even while the field is empty.
   * Every other field on this screen earns them by carrying a value, which is right when the
   * label can sit over an empty box as placeholder text. A field standing beside a date input
   * cannot: that control always renders its own dd-mm-yyyy mask, so it is permanently floated,
   * and an unfloated neighbour reads as a different kind of control rather than an empty one. */
  return (
    <div
      className={`nf${value || isAlwaysFloating ? ' float on' : ''}${isInvalid ? ' bad' : ''}${
        isDisabled ? ' off' : ''
      }`}
      style={{ width }}
    >
      <span className='mini'>{label}</span>
      <input
        aria-label={label}
        autoComplete='off'
        disabled={isDisabled}
        inputMode={isNumeric ? 'numeric' : undefined}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onCommit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            onCommit();
          }
        }}
      />
      {/* The clear button goes with the input: a disabled field the operator can still empty
          reads as half-enabled. */}
      <button
        type='button'
        className='dx'
        disabled={isDisabled}
        onClick={() => onChange('')}
        aria-label={`Clear ${label.toLowerCase()}`}
      >
        <ClearIcon />
      </button>
    </div>
  );
};

TextField.propTypes = {
  label: PropTypes.string.isRequired,
  isAlwaysFloating: PropTypes.bool,
  value: PropTypes.string,
  width: PropTypes.number,
  isNumeric: PropTypes.bool,
  isInvalid: PropTypes.bool,
  isDisabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onCommit: PropTypes.func,
};

TextField.defaultProps = {
  isAlwaysFloating: false,
  value: '',
  width: 212,
  isNumeric: false,
  isInvalid: false,
  isDisabled: false,
  onCommit: () => {},
};

export const SearchSelectField = (props) => {
  const { label, value, options, width, placeholder, onChange } = props;
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const selected = options.find((option) => option.value === value);

  /* The typed query is local state, so a filter cleared from outside -- "Clear filters", or the
   * post-commit reset -- would otherwise leave the old text in the box, reading as a filter that
   * is still applied when it is not. Only fires when value actually changes, so it cannot wipe
   * what is being typed before a pick. */
  useEffect(() => {
    if (!value) setQuery('');
  }, [value]);

  const typed = query.trim().toLowerCase();
  const matches = typed
    ? options.filter((option) => option.label.toLowerCase().includes(typed))
    : options;

  const pick = (option) => {
    setQuery('');
    setIsOpen(false);
    onChange(option.value);
  };

  return (
    <div className='dwrap'>
      <div className={`nf${selected || query ? ' float on' : ''}`} style={{ width }}>
        <span className='mini'>{label}</span>
        <input
          aria-label={label}
          autoComplete='off'
          placeholder={selected ? selected.label : placeholder}
          value={query}
          onFocus={() => setIsOpen(true)}
          onChange={(event) => {
            setQuery(event.target.value);
            setIsOpen(true);
          }}
          onBlur={() => setTimeout(() => setIsOpen(false), 160)}
        />
        {(selected || query) && (
          <button
            type='button'
            className='dx'
            aria-label={`Clear ${label.toLowerCase()}`}
            onClick={() => {
              setQuery('');
              onChange('');
            }}
          >
            <ClearIcon />
          </button>
        )}
      </div>

      <div className={`sug${isOpen ? ' up' : ''}`} role='listbox' aria-label={label}>
        {matches.map((option) => (
          <button
            type='button'
            role='option'
            aria-selected={option.value === value}
            key={option.value}
            onMouseDown={() => pick(option)}
          >
            <span className='sn'>{option.label}</span>
          </button>
        ))}
        {!matches.length && <div className='none'>{`No ${label.toLowerCase()} matches that.`}</div>}
      </div>
    </div>
  );
};

SearchSelectField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  width: PropTypes.number,
  placeholder: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

SearchSelectField.defaultProps = {
  value: '',
  options: [],
  width: 196,
  placeholder: 'All',
};

const MIN_QUERY_LENGTH = 2;

export const BulkOverrideDrugField = (props) => {
  const {
    label,
    width,
    placeholder,
    product,
    options,
    isLoading,
    showPickedCard,
    suggestionWidth,
    autoFocus,
    isRequired,
    minChars,
    isDisabled,
    disabledHint,
    onInput,
    onPick,
    onClear,
  } = props;

  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  /* The typed query is local state, so a product cleared from outside -- "Select another drug",
   * or a reset -- would otherwise leave the old name sitting in the box. Only fires when product
   * actually changes, so it cannot wipe what is being typed before a pick. */
  useEffect(() => {
    if (!product) setQuery('');
  }, [product]);

  const hits = options.filter((option) => {
    const q = query.trim().toLowerCase();
    return (
      option.displayName?.toLowerCase().includes(q) || option.productId?.toLowerCase().includes(q)
    );
  });
  /* Every match is listed -- the catalog search already caps its own result set, and .sug
   * scrolls, so truncating here only hid drugs the operator had already narrowed down to. */
  const shown = hits;
  const isSuggesting = isOpen && !isDisabled && query.trim().length >= MIN_QUERY_LENGTH;
  /* Below the search threshold nothing was ever asked for, so “no drug matches” would be
   * a claim about a search that never ran. */
  const isBelowMinChars = query.length < minChars;

  const handleInput = (value) => {
    setQuery(value);
    setIsOpen(true);
    onInput(value);
  };

  const handlePick = (option) => {
    setQuery(option.displayName);
    setIsOpen(false);
    onPick(option);
  };

  const handleClear = () => {
    setQuery('');
    setIsOpen(false);
    onClear();
  };

  const boxClass = [
    'nf',
    isDisabled ? 'off' : '',
    query || product ? 'float' : '',
    product ? 'on' : '',
    product && showPickedCard ? 'picked' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className='dwrap'>
      <div className={boxClass} style={{ width }}>
        <span className='mini'>
          {label}
          {isRequired && (
            <>
              {' '}
              <span className='req'>*</span>
            </>
          )}
        </span>
        <input
          ref={inputRef}
          placeholder={isDisabled ? disabledHint : placeholder}
          aria-label={label}
          autoComplete='off'
          disabled={isDisabled}
          value={product && showPickedCard ? '' : query}
          onChange={(event) => handleInput(event.target.value)}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setTimeout(() => setIsOpen(false), 160)}
        />
        {showPickedCard && (
          <div className='dsel' aria-hidden={!product}>
            <b>{product?.displayName}</b>
            <span>{product ? `Product ID ${product.productId}` : ''}</span>
          </div>
        )}
        {(query || product) && (
          <button type='button' className='dx' onClick={handleClear} aria-label={`Clear ${label}`}>
            <ClearDrugIcon />
          </button>
        )}
      </div>

      <div
        className={`sug${isSuggesting ? ' up' : ''}`}
        role='listbox'
        aria-label={`Matching ${label.toLowerCase()}s`}
        style={suggestionWidth ? { width: suggestionWidth } : undefined}
      >
        {isSuggesting && shown.length > 0 && (
          <>
            {shown.map((option) => (
              <button
                type='button'
                role='option'
                aria-selected={option.productId === product?.productId}
                key={option.productId}
                onMouseDown={() => handlePick(option)}
              >
                <span className='sn'>
                  {option.displayName}
                  <span className='sm'>
                    <b className='mono'>{`Product ID ${option.productId}`}</b>
                    {/* POM-6309: informational only — bulk override works on orders that already
                        exist, so a product withdrawn from ordering is still a valid target. */}
                    {option.isCatalogEnabled === false && (
                      <b className='catalog-disabled'>{CATALOG_DISABLED_LABEL}</b>
                    )}
                  </span>
                </span>
              </button>
            ))}
          </>
        )}
        {isSuggesting && !shown.length && (
          <div className='none'>
            {isLoading && 'Searching…'}
            {!isLoading && isBelowMinChars && `Keep typing — ${minChars} characters minimum.`}
            {!isLoading && !isBelowMinChars && `No drug matches “${query.trim()}”.`}
          </div>
        )}
      </div>
    </div>
  );
};

BulkOverrideDrugField.propTypes = {
  label: PropTypes.string,
  width: PropTypes.number,
  placeholder: PropTypes.string,
  product: PropTypes.shape({ productId: PropTypes.string, displayName: PropTypes.string }),
  autoFocus: PropTypes.bool,
  isRequired: PropTypes.bool,
  minChars: PropTypes.number,
  isDisabled: PropTypes.bool,
  disabledHint: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  isLoading: PropTypes.bool,
  showPickedCard: PropTypes.bool,
  suggestionWidth: PropTypes.number,
  onInput: PropTypes.func.isRequired,
  onPick: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};

BulkOverrideDrugField.defaultProps = {
  label: 'Drug',
  width: 352,
  placeholder: 'Search by drug name',
  product: null,
  options: [],
  isLoading: false,
  showPickedCard: true,
  suggestionWidth: null,
  autoFocus: false,
  isRequired: true,
  minChars: 0,
  isDisabled: false,
  disabledHint: '',
};

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const iso = (year, month, day) =>
  `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

const fmt = (value) => {
  const [year, month, day] = value.split('-');
  return `${Number(day)} ${MONTHS[Number(month) - 1].slice(0, 3)} ${year}`;
};

const triggerText = (from, to) => {
  if (!from) return 'All dates';
  if (!to) return `${fmt(from)} → select end`;
  return from === to ? fmt(from) : `${fmt(from)} – ${fmt(to)}`;
};

export const BulkOverrideDateRange = ({ from, to, width, onChange }) => {
  const today = new Date();
  const [isOpen, setIsOpen] = useState(false);
  // the range being picked; nothing is filtered until Done publishes it
  const [draft, setDraft] = useState({ from, to });
  const [cursor, setCursor] = useState({ year: today.getFullYear(), month: today.getMonth() });

  const firstWeekday = new Date(cursor.year, cursor.month, 1).getDay();
  const dayCount = new Date(cursor.year, cursor.month + 1, 0).getDate();

  const navigate = (step) => {
    let month = cursor.month + step;
    let { year } = cursor;
    if (month < 0) {
      month = 11;
      year -= 1;
    }
    if (month > 11) {
      month = 0;
      year += 1;
    }
    setCursor({ year, month });
  };

  const pickDay = (value) => {
    if (!draft.from || draft.to) {
      setDraft({ from: value, to: null });
      return;
    }
    setDraft(
      value < draft.from ? { from: value, to: draft.from } : { from: draft.from, to: value }
    );
  };

  const clear = (event) => {
    event.stopPropagation();
    setDraft({ from: null, to: null });
    onChange({ from: null, to: null });
    setIsOpen(false);
  };

  const applyDraft = () => {
    onChange(draft);
    setIsOpen(false);
  };

  return (
    <div className='drwrap'>
      <div className={`nf float sel${from ? ' on' : ''}`} style={{ width }}>
        <span className='mini'>Date Range</span>
        <button type='button' className='dx' onClick={clear} aria-label='Clear date range'>
          <ClearIcon />
        </button>
        <button
          type='button'
          className='dtrig'
          aria-haspopup='dialog'
          onClick={() => {
            setDraft({ from, to });
            setIsOpen((open) => !open);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') setIsOpen(false);
          }}
        >
          <span>{triggerText(from, to)}</span>
          <ChevronIcon />
        </button>
      </div>

      <div className={`cal${isOpen ? ' up' : ''}`} role='dialog' aria-label='Select a date range'>
        <div className='cal-h'>
          <button type='button' onClick={() => navigate(-1)} aria-label='Previous month'>
            &lsaquo;
          </button>
          <b>{`${MONTHS[cursor.month]} ${cursor.year}`}</b>
          <button type='button' onClick={() => navigate(1)} aria-label='Next month'>
            &rsaquo;
          </button>
        </div>

        <div className='cal-g'>
          {WEEKDAYS.map((day, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <span className='wd' key={`wd${index}`}>
              {day}
            </span>
          ))}
          {Array.from({ length: firstWeekday }, (unused, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <span key={`pad${index}`} />
          ))}
          {Array.from({ length: dayCount }, (unused, index) => {
            const value = iso(cursor.year, cursor.month, index + 1);
            const isEnd = value === draft.from || value === draft.to;
            const isMid = draft.from && draft.to && value > draft.from && value < draft.to;

            return (
              <button
                type='button'
                className={`cd${isEnd ? ' on' : ''}${isMid ? ' mid' : ''}`}
                key={value}
                onClick={() => pickDay(value)}
              >
                {index + 1}
              </button>
            );
          })}
        </div>

        <div className='cal-f'>
          <button type='button' className='rmall' onClick={clear}>
            Clear
          </button>
          <div className='sp' />
          <button
            type='button'
            className='btn btn-b'
            disabled={Boolean(draft.from) && !draft.to}
            onClick={applyDraft}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

BulkOverrideDateRange.propTypes = {
  from: PropTypes.string,
  to: PropTypes.string,
  width: PropTypes.number,
  onChange: PropTypes.func.isRequired,
};

BulkOverrideDateRange.defaultProps = {
  from: null,
  to: null,
  width: 252,
};

/* ---------- BulkOverrideFilterRow : tier-1 / tier-2 filter rows ---------- */

/* Review mode has no drug scope, so the prototype folds the drug field and the
 * date range into the single advanced-filter block and drops the tier-1 row --
 * see placeDrugField() in the prototype. With no drug scope there is no NDC
 * list to offer either, so review mode types the NDC instead of picking it. */
const BulkOverrideFilterRow = (props) => {
  const {
    isReview,
    product,
    drugOptions,
    isDrugLoading,
    filters,
    facilityOptions,
    partnerOptions,
    ndcOptions,
    onDrugInput,
    onDrugChange,
    onFilterChange,
  } = props;

  const drugField = (
    <BulkOverrideDrugField
      width={352}
      autoFocus={!product}
      isRequired
      placeholder='Search by drug name'
      product={product}
      options={drugOptions}
      isLoading={isDrugLoading}
      minChars={MIN_DRUG_SEARCH_LENGTH}
      onInput={onDrugInput}
      onPick={onDrugChange}
      onClear={() => onDrugChange(null)}
    />
  );

  const dateRange = (
    <BulkOverrideDateRange
      width={isReview ? 216 : 252}
      from={filters.from}
      to={filters.to}
      onChange={({ from, to }) => onFilterChange('dateRange', { from, to })}
    />
  );

  return (
    <>
      {/* Every mode opens on the drug. In review it narrows a queue rather than scoping it, so it
          stays optional there -- the list loads without one and the other filters still apply. */}
      <div className='mtools tier1'>
        {drugField}
        <div className='sp' />
        {dateRange}
      </div>

      <div className='mtools tier2'>
        <span className='tlabel'>Advanced filters</span>
        <SelectField
          label='Facility'
          width={196}
          value={filters.facility}
          options={facilityOptions}
          onChange={(value) => onFilterChange('facility', value)}
        />
        <SearchSelectField
          label='Partner'
          width={238}
          value={filters.partner}
          options={partnerOptions}
          onChange={(value) => onFilterChange('partner', value)}
        />
        {/* Review does not scope to a drug, so with none picked the catalog cannot offer a list
            and the field has to be typeable. Pick a drug and it becomes that drug's NDCs. */}
        {isReview && !ndcOptions.length ? (
          <TextField
            label='Current NDCs'
            width={190}
            value={filters.ndc}
            onChange={(value) => onFilterChange('ndc', value)}
          />
        ) : (
          <SelectField
            label='Current NDCs'
            width={190}
            isMono
            value={filters.ndc}
            options={ndcOptions}
            onChange={(value) => onFilterChange('ndc', value)}
          />
        )}
        <TextField
          label='Qty'
          width={92}
          isNumeric
          value={filters.qty}
          onChange={(value) => onFilterChange('qty', value)}
        />
        <TextField
          label='Order Id'
          width={isReview ? 176 : 212}
          value={filters.orderId}
          onChange={(value) => onFilterChange('orderId', value)}
        />
      </div>
    </>
  );
};

BulkOverrideFilterRow.propTypes = {
  isReview: PropTypes.bool,
  product: PropTypes.shape({}),
  drugOptions: PropTypes.arrayOf(PropTypes.shape({})),
  isDrugLoading: PropTypes.bool,
  filters: PropTypes.shape({
    facility: PropTypes.string,
    partner: PropTypes.string,
    ndc: PropTypes.string,
    qty: PropTypes.string,
    orderId: PropTypes.string,
    from: PropTypes.string,
    to: PropTypes.string,
  }).isRequired,
  facilityOptions: PropTypes.arrayOf(PropTypes.shape({})),
  partnerOptions: PropTypes.arrayOf(PropTypes.shape({})),
  ndcOptions: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })
  ),
  onDrugInput: PropTypes.func.isRequired,
  onDrugChange: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
};

BulkOverrideFilterRow.defaultProps = {
  isReview: false,
  product: null,
  drugOptions: [],
  isDrugLoading: false,
  facilityOptions: [],
  partnerOptions: [],
  ndcOptions: [],
};

/* ---------- BulkOverrideApplyBar : apply-to-selection bar ---------- */

const ScopeLabel = ({ count, ndcs, showNdcs }) => {
  if (!count) {
    return (
      <span className='lb'>
        <b>
          <span className='none'>No orders selected yet</span>
        </b>
      </span>
    );
  }

  const orders = `${count} order${count === 1 ? '' : 's'}`;

  return (
    <span className='lb'>
      <b>
        <span className='hl'>{orders}</span>
        {showNdcs && ndcs.length === 1 && (
          <>
            {' on '}
            <span className='hl mono'>{ndcs[0]}</span>
          </>
        )}
        {showNdcs && ndcs.length > 1 && (
          <>
            {' on '}
            <span className='hl'>{`${ndcs.length} different NDCs`}</span>
          </>
        )}
      </b>
    </span>
  );
};

ScopeLabel.propTypes = {
  count: PropTypes.number.isRequired,
  ndcs: PropTypes.arrayOf(PropTypes.string),
  showNdcs: PropTypes.bool,
};

ScopeLabel.defaultProps = { ndcs: [], showNdcs: false };

const PICK_DRUG_FIRST = 'Select the new drug first…';

const BulkOverrideApplyBar = (props) => {
  const {
    note,
    isReview,
    isProductChange,
    isUnfulfillment,
    isQuantityChange,
    isFillLaterSplit,
    isFillLaterMove,
    schedule,
    scheduleIssue,
    onScheduleLineChange,
    onScheduleLineAdd,
    onScheduleLineRemove,
    moveDate,
    moveDateIssue,
    onMoveDateChange,
    selectedCount,
    selectedNdcs,
    valueLabel,
    valueOptions,
    placeholder,
    value,
    isValueRequired,
    isValuePickerReady,
    targetProduct,
    drugOptions,
    isDrugLoading,
    onTargetProductChange,
    onDrugInput,
    onValueChange,
    onApply,
    showQuantity,
    quantityValue,
    filteredQuantity,
    onQuantityChange,
    isQuantityGateUnmet,
  } = props;

  const hasSelection = selectedCount > 0;
  /* The picker disables this option, but a value chosen before a filter narrowed the list can
   * still be sitting in it, so the action is gated too rather than only the menu. */
  const isValueNoOp = valueOptions.some((option) => option.value === value && option.isNoOp);
  const isQuantityUsable = showQuantity && isStagedQuantity(quantityValue);
  /* A split has no single quantity field to gate on -- its quantities live per schedule line --
   * so what makes it stampable is a schedule with at least one complete line and no problem of
   * its own. Without this the button stays dead, because quantityValue is never set here. */
  const isScheduleStampable = completeLines(schedule).length > 0 && !scheduleIssue;
  const isMoveDateStampable = isRealCalendarDay(moveDate) && !moveDateIssue;
  const isQuantityRefused = showQuantity && quantityValue !== '' && !isQuantityUsable;
  const tip = hasSelection ? undefined : 'Select the orders first';

  const [title, ...rest] = note.split('. ');
  const orders = `${selectedCount} order${selectedCount === 1 ? '' : 's'}`;
  /* Quantity mode says nothing about NDCs. It does not read one, write one or move one, so
     "22 orders on 2 different NDCs" was describing a dimension of the selection the operation
     cannot touch. The NDC breakdown belongs to the operations that DO change it. */
  const selectionSummary =
    isReview ||
    isUnfulfillment ||
    isQuantityChange ||
    isFillLaterSplit ||
    isFillLaterMove ||
    !selectedNdcs.length
      ? orders
      : `${orders} on ${
          selectedNdcs.length === 1 ? selectedNdcs[0] : `${selectedNdcs.length} different NDCs`
        }`;

  return (
    <div className={`applywrap${isFillLaterSplit ? ' sched' : ''}`}>
      <div className='ab-ic'>
        <ClipboardIcon />
      </div>

      <div className='ab-copy'>
        <b>{`${title}.`}</b>
        <span>{renderMarks(rest.join('. '))}</span>
        {isFillLaterSplit && (
          <span className='ab-copy-hint'>
            {`Dates are suggested ${FILL_LATER_LEAD_TIME_DAYS} days apart and can be changed to ` +
              'any day after today. Each order is created once the item before it ships.'}
          </span>
        )}
      </div>

      <div className='ab-sep' />

      <div className='ab-count'>
        <i>Selected orders</i>
        <div>
          <b className={hasSelection ? 'on' : undefined}>{selectedCount}</b>
          <span>{hasSelection ? selectionSummary : 'No orders selected yet'}</span>
        </div>
      </div>

      {isProductChange && (
        <div className='ab-pick pwrap'>
          <BulkOverrideDrugField
            label='New drug'
            width={300}
            placeholder='Search any product'
            showPickedCard
            product={targetProduct}
            options={drugOptions}
            isLoading={isDrugLoading}
            minChars={MIN_DRUG_SEARCH_LENGTH}
            isDisabled={!hasSelection}
            disabledHint='Select the orders first'
            onInput={onDrugInput}
            onPick={onTargetProductChange}
            onClear={() => onTargetProductChange(null)}
          />
        </div>
      )}

      {/* Quantity mode has no NDC to pick — the drug and its NDC are exactly what it leaves
          alone — so the picker is absent rather than present and disabled. A split leaves both
          alone too; what it needs instead is the date, rendered below. */}
      {!isUnfulfillment && !isQuantityChange && !isFillLaterSplit && !isFillLaterMove && (
        <div className='ab-pick'>
          <div className='fieldpair tip' data-tip={tip}>
            <span className='mini'>
              {valueLabel}
              {isValueRequired && (
                <>
                  {' '}
                  <span className='req'>*</span>
                </>
              )}
            </span>
            <select
              aria-label={valueLabel}
              style={{ width: isProductChange ? 200 : 230 }}
              disabled={!isValuePickerReady || !valueOptions.length}
              value={value}
              onChange={(event) => onValueChange(event.target.value)}
            >
              <option value=''>{isValuePickerReady ? placeholder : PICK_DRUG_FIRST}</option>
              {/* the list opens so the drug's NDCs can be seen, but nothing can be
                  picked until orders are selected */}
              {valueOptions.map((option) => (
                <option
                  value={option.value}
                  key={option.value}
                  disabled={!hasSelection || option.isNoOp}
                >
                  {option.isNoOp ? `${option.label} — no change` : option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Not shown for a split: its quantity belongs to a schedule line, beside the date it is
          being filled on, so a single field here would have nothing to set. */}
      {showQuantity && !isFillLaterSplit && (
        <div className='ab-pick'>
          <div
            className='fieldpair tip'
            data-tip={isQuantityGateUnmet ? 'Filter by the current quantity first' : undefined}
          >
            <TextField
              label='New quantity'
              width={150}
              isNumeric
              isDisabled={isQuantityGateUnmet}
              isInvalid={isQuantityRefused}
              value={quantityValue}
              onChange={onQuantityChange}
            />
          </div>
        </div>
      )}

      {/* The schedule Apply stamps onto the selection. Each row receives its own copy and can be
          changed inline afterwards, so this is a starting point rather than a batch-wide setting.
          Native date inputs, not BulkOverrideDateRange -- that is a two-ended range picker for
          FILTERING and would read as one here. */}
      {isFillLaterMove && (
        <div className='ab-pick ab-pick-wide'>
          <FillLaterMoveDateField
            value={moveDate}
            issue={moveDateIssue}
            disabledTip={tip}
            onChange={onMoveDateChange}
          />
        </div>
      )}

      {isFillLaterSplit && (
        <div className='ab-pick ab-pick-wide'>
          <FillLaterScheduleFields
            schedule={schedule}
            issue={scheduleIssue}
            dispenseQuantity={filteredQuantity}
            isDisabled={isQuantityGateUnmet}
            disabledTip={isQuantityGateUnmet ? 'Filter by the current quantity first' : tip}
            onLineChange={onScheduleLineChange}
            onAddLine={onScheduleLineAdd}
            onRemoveLine={onScheduleLineRemove}
          />
        </div>
      )}

      {!isUnfulfillment && (
        <div className='ab-act'>
          <button
            type='button'
            className='btn btn-b'
            disabled={
              !hasSelection ||
              isValueNoOp ||
              isQuantityGateUnmet ||
              (isFillLaterMove && !isMoveDateStampable) ||
              (isFillLaterSplit && !isScheduleStampable) ||
              (!isFillLaterSplit && !isFillLaterMove && !value && !isQuantityUsable)
            }
            onClick={onApply}
          >
            Apply to selected
          </button>
        </div>
      )}
    </div>
  );
};

BulkOverrideApplyBar.propTypes = {
  note: PropTypes.string.isRequired,
  isReview: PropTypes.bool,
  isProductChange: PropTypes.bool,
  isUnfulfillment: PropTypes.bool,
  isQuantityChange: PropTypes.bool,
  isFillLaterSplit: PropTypes.bool,
  isFillLaterMove: PropTypes.bool,
  schedule: PropTypes.arrayOf(PropTypes.shape({})),
  scheduleIssue: PropTypes.shape({ message: PropTypes.string, lineIndex: PropTypes.number }),
  onScheduleLineChange: PropTypes.func,
  onScheduleLineAdd: PropTypes.func,
  onScheduleLineRemove: PropTypes.func,
  moveDate: PropTypes.string,
  moveDateIssue: PropTypes.shape({ message: PropTypes.string }),
  onMoveDateChange: PropTypes.func,
  selectedCount: PropTypes.number.isRequired,
  selectedNdcs: PropTypes.arrayOf(PropTypes.string),
  valueLabel: PropTypes.string.isRequired,
  valueOptions: PropTypes.arrayOf(PropTypes.shape({})),
  placeholder: PropTypes.string.isRequired,
  value: PropTypes.string,
  isValueRequired: PropTypes.bool,
  isValuePickerReady: PropTypes.bool,
  targetProduct: PropTypes.shape({}),
  drugOptions: PropTypes.arrayOf(PropTypes.shape({})),
  isDrugLoading: PropTypes.bool,
  onTargetProductChange: PropTypes.func,
  onDrugInput: PropTypes.func,
  onValueChange: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  showQuantity: PropTypes.bool,
  quantityValue: PropTypes.string,
  filteredQuantity: PropTypes.string,
  onQuantityChange: PropTypes.func,
  isQuantityGateUnmet: PropTypes.bool,
};

BulkOverrideApplyBar.defaultProps = {
  isFillLaterSplit: false,
  isFillLaterMove: false,
  schedule: [],
  scheduleIssue: null,
  onScheduleLineChange: () => {},
  onScheduleLineAdd: () => {},
  onScheduleLineRemove: () => {},
  moveDate: '',
  moveDateIssue: null,
  onMoveDateChange: () => {},
  isQuantityGateUnmet: false,
  isReview: false,
  isValuePickerReady: true,
  isProductChange: false,
  isUnfulfillment: false,
  isQuantityChange: false,
  selectedNdcs: [],
  valueOptions: [],
  value: '',
  isValueRequired: false,
  targetProduct: null,
  drugOptions: [],
  isDrugLoading: false,
  onTargetProductChange: () => {},
  onDrugInput: () => {},
  showQuantity: false,
  quantityValue: '',
  filteredQuantity: '',
  onQuantityChange: () => {},
};

/* ---------- BulkOverrideTable : the grid and its footer ---------- */

const SKELETON_WIDTHS = [122, 104, 30, 26, 116, 118];
const SKELETON_ROWS = 9;

/* AG GRID SPIKE. The prototype's grid is a plain table; this trades an exact
 * pixel match for what inventory gets from AG Grid -- sorting, resizing, column
 * moving. The theme below pushes the prototype's own values into AG Grid so the
 * navy header, type and the staged-row colours survive. */
const boTheme = themeQuartz.withParams({
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  fontSize: 13,
  foregroundColor: '#1F2937',
  backgroundColor: '#fff',
  headerBackgroundColor: '#1B2A5E',
  headerTextColor: '#fff',
  headerFontSize: 11.5,
  headerFontWeight: 600,
  headerHeight: 42,
  rowHeight: 46,
  borderColor: '#F1F5F9',
  rowHoverColor: '#FAFAFA',
  selectedRowBackgroundColor: '#F5F8FF',
  accentColor: '#2563EB',
  wrapperBorder: true,
  wrapperBorderRadius: 8,
  cellHorizontalPadding: 14,
});

/* Volatile state travels by context, never through columnDefs. Rebuilding
 * columnDefs tears down and recreates cells, which detaches the very node the
 * operator is clicking -- selection then silently does nothing. */
const AG_ROW = PropTypes.shape({
  lineItemId: PropTypes.string,
  orderId: PropTypes.string,
  ndc: PropTypes.string,
  medicationName: PropTypes.string,
  quantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  patientName: PropTypes.string,
});

const GridStateContext = createContext(null);

const HeadCheckCell = () => {
  const { isAll, isSome, onToggleAll } = useContext(GridStateContext);

  return (
    <div
      className={`cb${isAll ? ' on' : ''}${!isAll && isSome ? ' mid' : ''}`}
      role='checkbox'
      aria-checked={isAll}
      aria-label='Select all orders'
      tabIndex={0}
      onClick={onToggleAll}
      onKeyDown={(event) => {
        if (event.key === ' ' || event.key === 'Enter') {
          event.preventDefault();
          onToggleAll();
        }
      }}
    >
      {!isAll && isSome ? <DashIcon /> : <TickIcon />}
    </div>
  );
};

const RowCheckCell = ({ data }) => {
  const { selectedIds, onToggleRow } = useContext(GridStateContext);
  const isOn = selectedIds.includes(data.lineItemId);

  return (
    <div
      className={`cb${isOn ? ' on' : ''}`}
      role='checkbox'
      aria-checked={isOn}
      aria-label={`Select order ${data.orderId}`}
      tabIndex={0}
      onClick={() => onToggleRow(data.lineItemId)}
      onKeyDown={(event) => {
        if (event.key === ' ' || event.key === 'Enter') {
          event.preventDefault();
          onToggleRow(data.lineItemId);
        }
      }}
    >
      <TickIcon />
    </div>
  );
};

RowCheckCell.propTypes = { data: AG_ROW.isRequired };

const OrderIdCell = ({ value }) => <span className='oid mono'>{value}</span>;
OrderIdCell.propTypes = { value: PropTypes.string };
OrderIdCell.defaultProps = { value: '' };

const RxCell = ({ value }) => <span className='mono dim'>{value}</span>;
RxCell.propTypes = { value: PropTypes.string };
RxCell.defaultProps = { value: '' };

const DrugCell = ({ data }) => (
  <div className='stack'>
    <b>{data.medicationName}</b>
    <span className='mono'>{data.ndc}</span>
  </div>
);
DrugCell.propTypes = { data: AG_ROW.isRequired };

/* Product change swaps the drug as well as the NDC, so the row has to show both sides
 * of it -- the NDC alone does not tell you what you are moving away from. */
const CurrentDrugCell = ({ data }) => {
  const { isRowChanged } = useContext(GridStateContext);

  return (
    <div className={`stack${isRowChanged(data) ? ' was' : ''}`}>
      <b>{data.medicationName}</b>
      <span className='mono'>{data.ndc}</span>
    </div>
  );
};
CurrentDrugCell.propTypes = { data: AG_ROW.isRequired };

const CurrentNdcCell = ({ data }) => {
  const { isRowChanged } = useContext(GridStateContext);

  return <span className={`mono${isRowChanged(data) ? ' was' : ''}`}>{data.ndc}</span>;
};
CurrentNdcCell.propTypes = { data: AG_ROW.isRequired };

const ValueCell = ({ data }) => {
  const {
    stagedValues,
    valueOptions,
    placeholder,
    isValuePickerReady,
    isReview,
    isProductChange,
    targetProductName,
    onRowValueChange,
    onRowFieldChange,
  } = useContext(GridStateContext);
  const staged = stagedValues[data.lineItemId];
  const isSet = hasStagedValue(staged);

  /* No editor until the row has actually been staged. The operator picks the target drug and NDC
   * in the apply bar and applies it to the selection; only then does a row have something to
   * refine. Before that every row was carrying a disabled select and a "no target drug yet" line,
   * which is 65 rows of furniture saying nothing. An unstaged row is not moving, so it says so. */
  if (isProductChange) {
    if (!isSet) return <span className='nomove'>Not moving</span>;

    const edit = staged || EMPTY_ROW_EDIT;

    return (
      <div className='cell set stacked'>
        {targetProductName ? <span className='newdrug'>{targetProductName}</span> : null}
        <select
          className={`rown mono${edit.ndc ? '' : ' empty'}`}
          aria-label={`New NDC for order ${data.orderId}`}
          style={{ width: '100%' }}
          disabled={!isValuePickerReady}
          value={edit.ndc || ''}
          onChange={(event) =>
            onRowFieldChange({
              lineItemId: data.lineItemId,
              field: 'ndc',
              value: event.target.value,
            })
          }
        >
          <option value=''>{isValuePickerReady ? placeholder : PICK_DRUG_FIRST}</option>
          {valueOptions.map((option) => (
            <option value={option.value} key={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div className={`cell${isSet ? ' set' : ''}`}>
      <select
        className={`rown${isReview ? '' : ' mono'}${staged ? '' : ' empty'}`}
        aria-label={`Value for order ${data.orderId}`}
        style={{ width: '100%' }}
        disabled={!isValuePickerReady}
        value={staged || ''}
        onChange={(event) =>
          onRowValueChange({ lineItemId: data.lineItemId, value: event.target.value })
        }
      >
        <option value=''>{isValuePickerReady ? placeholder : PICK_DRUG_FIRST}</option>
        {valueOptions.map((option) => {
          const isSameNdc = !isReview && ndcDigits(option.value) === ndcDigits(data.ndc);

          return (
            <option value={option.value} key={option.value} disabled={isSameNdc}>
              {isSameNdc ? `${option.label} — no change` : option.label}
            </option>
          );
        })}
      </select>
    </div>
  );
};
ValueCell.propTypes = { data: AG_ROW.isRequired };

const boDefaultColDef = {
  sortable: true,
  resizable: true,
  suppressMovable: false,
  suppressHeaderMenuButton: true,
};

const CHECK_COLUMN = {
  headerComponent: HeadCheckCell,
  cellRenderer: RowCheckCell,
  cellClass: 'bo-cbcell',
  headerClass: 'bo-cbcell',
  width: 52,
  minWidth: 52,
  sortable: false,
  resizable: false,
  suppressMovable: true,
  pinned: 'left',
};

const ORDER_ID_COLUMN = {
  field: 'orderId',
  headerName: 'Order Id',
  cellRenderer: OrderIdCell,
  flex: 1,
  minWidth: 120,
};

const RX_COLUMN = {
  field: 'rxNumber',
  headerName: 'Rx Number',
  cellRenderer: RxCell,
  flex: 1,
  minWidth: 120,
};

const DRUG_COLUMN = {
  field: 'medicationName',
  headerName: 'Drug',
  cellRenderer: DrugCell,
  flex: 1,
  minWidth: 200,
  autoHeight: true,
};

const CURRENT_NDC_COLUMN = {
  field: 'ndc',
  headerName: 'Current NDC',
  cellRenderer: CurrentNdcCell,
  flex: 1,
  minWidth: 140,
};

const NDC_COLUMN = {
  field: 'ndc',
  headerName: 'NDC',
  cellRenderer: RxCell,
  flex: 1,
  minWidth: 140,
};

const DRUG_NAME_COLUMN = {
  field: 'medicationName',
  headerName: 'Drug',
  flex: 1,
  minWidth: 200,
};

const CURRENT_DRUG_COLUMN = {
  field: 'medicationName',
  headerName: 'Current drug / NDC',
  cellRenderer: CurrentDrugCell,
  flex: 1,
  minWidth: 190,
  autoHeight: true,
};

/* Reads only. A staged quantity is shown in its own column beside this one -- same treatment the
 * NDC gets -- so the operator can see what a row is going from and to while deciding to commit. */
const QtyCell = ({ data }) => {
  const { stagedValues } = useContext(GridStateContext);
  const staged = stagedValues[data.lineItemId];
  const isReplaced =
    typeof staged === 'object' &&
    staged !== null &&
    isStagedQuantity(staged.quantity) &&
    Number(staged.quantity) !== Number(data.quantity);

  return <span className={isReplaced ? 'was' : undefined}>{data.quantity}</span>;
};
QtyCell.propTypes = { data: AG_ROW.isRequired };

/* Blank rather than absent when a quantity cannot be edited: the column list may only vary on
 * things that cannot change while the grid is mounted, and the qty filter changes on a keystroke.
 * Rebuilding columnDefs would detach the cells and the editors would quietly stop responding. */
const NewQtyCell = ({ data }) => {
  const { stagedValues, isQtyEditable, onRowFieldChange } = useContext(GridStateContext);
  const edit = stagedValues[data.lineItemId] || EMPTY_ROW_EDIT;
  const isValid = isStagedQuantity(edit.quantity);
  const isRefused = edit.quantity !== '' && !isValid;

  /* Read-only when the quantity cannot be edited -- no target product yet, or the filter that
   * opened this column has since been cleared. A staged value still has to be readable: the Qty
   * column strikes the old one through and the commit counts it either way. */
  if (!isQtyEditable) {
    return isValid ? (
      <div className='cell set'>
        <b>{edit.quantity}</b>
      </div>
    ) : (
      <span className='nomove'>&mdash;</span>
    );
  }

  return (
    <div className={`cell${isValid ? ' set' : ''}`}>
      <input
        className={`rowq${isRefused ? ' bad' : ''}`}
        type='text'
        inputMode='numeric'
        autoComplete='off'
        title={isRefused ? 'Enter a whole number greater than 0' : undefined}
        aria-invalid={isRefused}
        aria-label={`New quantity for order ${data.orderId}`}
        placeholder={String(data.quantity ?? '')}
        value={edit.quantity}
        onChange={(event) =>
          onRowFieldChange({
            lineItemId: data.lineItemId,
            field: 'quantity',
            value: event.target.value,
          })
        }
      />
    </div>
  );
};
NewQtyCell.propTypes = { data: AG_ROW.isRequired };

/* All three split cells are DERIVED from the row's staged schedule -- none of them is typed into.
 * The schedule is built once in the apply bar and stamped across the selection, so the grid's job
 * here is to show what each row received and, where the row cannot take it, why. */

const rowSchedule = (stagedValues, data) => stagedValues[data.lineItemId]?.splits;

const rowProblem = (stagedValues, data) =>
  rowScheduleProblem({
    schedule: rowSchedule(stagedValues, data),
    currentQuantity: data.quantity,
  });

/* What the whole schedule takes off this row. The sum, not one figure: the server validates
 * `requestedTotal` against the dispense quantity the same way. */
const SplitTotalCell = ({ data }) => {
  const { stagedValues } = useContext(GridStateContext);
  const schedule = rowSchedule(stagedValues, data);

  if (completeLines(schedule).length === 0) return <span className='nomove'>Not split</span>;

  const total = scheduleTotal(schedule);
  const problem = rowProblem(stagedValues, data);

  return (
    <div className={`cell${problem ? '' : ' set'}`}>
      <b className={problem ? 'bad' : undefined}>{total}</b>
    </div>
  );
};
SplitTotalCell.propTypes = { data: AG_ROW.isRequired };

/* Derived, never typed. Showing what is left beside the total is what makes the arithmetic
 * visible -- the operator schedules 35 of a 50 and sees the original keep 15 without doing the
 * sum. Negative is shown rather than hidden: that IS the reason the row is refused. */
const RetainedQtyCell = ({ data }) => {
  const { stagedValues } = useContext(GridStateContext);
  const schedule = rowSchedule(stagedValues, data);

  if (completeLines(schedule).length === 0) return <span className='nomove'>&mdash;</span>;

  const remainder = retainedAfterSchedule({ schedule, currentQuantity: data.quantity });

  if (remainder === null) {
    const shortfall = Number(data.quantity) - scheduleTotal(schedule);

    return (
      <div className='cell'>
        <b className='bad'>{Number.isFinite(shortfall) ? shortfall : '—'}</b>
      </div>
    );
  }

  return (
    <div className='cell set'>
      <b>{remainder}</b>
    </div>
  );
};
RetainedQtyCell.propTypes = { data: AG_ROW.isRequired };

/* The schedule this row actually received, one line per split. The apply bar stamps the same
 * dates across the selection, so without this the operator cannot see that a row they adjusted
 * on its own now differs from the rest. */
const ScheduleDatesCell = ({ data }) => {
  const { stagedValues } = useContext(GridStateContext);
  const lines = completeLines(rowSchedule(stagedValues, data));

  if (lines.length === 0) return <span className='nomove'>&mdash;</span>;

  const problem = rowProblem(stagedValues, data);

  return (
    <div className={`cell scheddates${problem ? ' bad' : ' set'}`}>
      <div className='schedlist'>
        {lines.map((line) => (
          <span className='schedpair' key={`${line.quantity}-${line.scheduledFor}`}>
            <b>{line.quantity}</b> <span className='schedqty'>qty</span>
            {` on ${formatScheduleDay(line.scheduledFor)}`}
          </span>
        ))}
      </div>
    </div>
  );
};
ScheduleDatesCell.propTypes = { data: AG_ROW.isRequired };

const MoveDateCell = ({ data }) => {
  const { stagedValues } = useContext(GridStateContext);
  const scheduledFor = stagedValues[data.lineItemId]?.scheduledFor;

  if (!isRealCalendarDay(scheduledFor)) return <span className='nomove'>Not scheduled</span>;

  const away = relativeScheduleDay(scheduledFor);

  return (
    <div className='cell scheddates set'>
      <span className='schedpair'>
        <b>{formatScheduleDay(scheduledFor)}</b>
        {away ? ` · ${away}` : ''}
      </span>
    </div>
  );
};
MoveDateCell.propTypes = { data: AG_ROW.isRequired };

/* transformBulkLabels already flattens patient.patientDemographics into a formatted
 * "Lastname, Firstname" on the row (api/requests/transform.js), so there is nothing to render
 * here -- reading data.patient would find the key the transform destructured away. */
const PATIENT_NAME_COLUMN = {
  field: 'patientName',
  headerName: 'Patient',
  flex: 1,
  minWidth: 150,
};

const SPLIT_QTY_COLUMN = {
  colId: 'splitQty',
  headerName: 'Quantity to fill later',
  cellRenderer: SplitTotalCell,
  width: 152,
  minWidth: 132,
  maxWidth: 176,
  sortable: false,
};

const RETAINED_QTY_COLUMN = {
  colId: 'retainedQty',
  headerName: 'Remaining quantity',
  cellRenderer: RetainedQtyCell,
  width: 146,
  minWidth: 126,
  maxWidth: 168,
  sortable: false,
};

const FILL_DATES_COLUMN = {
  colId: 'fillDates',
  headerName: 'No. of splits',
  cellRenderer: ScheduleDatesCell,
  width: 190,
  minWidth: 160,
  sortable: false,
};

const MOVE_DATE_COLUMN = {
  colId: 'moveDate',
  headerName: 'Fill later date',
  cellRenderer: MoveDateCell,
  width: 210,
  minWidth: 170,
  sortable: false,
};

const PARTNER_COLUMN = { field: 'partner', headerName: 'Partner', flex: 1, minWidth: 130 };
const QTY_COLUMN = {
  field: 'quantity',
  headerName: 'Qty',
  cellRenderer: QtyCell,
  flex: 1,
  minWidth: 80,
};
/* Product change shows the quantity twice, so the pair is fixed and narrow there: a quantity is a
 * few digits, and the space belongs to the drug and NDC columns, whose names wrap. Elsewhere
 * QTY_COLUMN keeps flexing, as it has since before this screen carried a second one. */
const QTY_COLUMN_ID = 'qty';

const QTY_COLUMN_PAIRED = {
  colId: QTY_COLUMN_ID,
  field: 'quantity',
  headerName: 'Qty',
  cellRenderer: QtyCell,
  width: 80,
  minWidth: 72,
  maxWidth: 104,
};

const NEW_QTY_COLUMN_ID = 'newQty';

const NEW_QTY_COLUMN = {
  colId: NEW_QTY_COLUMN_ID,
  headerName: 'New qty',
  cellRenderer: NewQtyCell,
  width: 132,
  minWidth: 118,
  maxWidth: 160,
  sortable: false,
};
const FACILITY_COLUMN = { field: 'facilityName', headerName: 'Facility', flex: 1, minWidth: 110 };

/* The centred state that stands in for the grid. Matches the landing state in
 * ManageShipmentItemsDialog -- same fade-up, same circle, same proportions -- built in this
 * screen's own CSS rather than MUI so it sits with the rest of the ported design. */
const BulkOverrideGridState = (props) => {
  const { tone, icon: Icon, title, body, actionLabel, onAction } = props;

  return (
    <div className={`gstate${tone ? ` ${tone}` : ''}`}>
      <span className='gs-ic'>
        <Icon />
      </span>
      <b>{title}</b>
      <span>{body}</span>
      {actionLabel && (
        <button type='button' className='btn gs-act' onClick={onAction}>
          {actionLabel}
        </button>
      )}
    </div>
  );
};

/* Every state that stands in for the rows keeps the column header, so the frame does not
 * appear and disappear between loading, empty and error. Headers come off columnDefs rather
 * than a hand-written list -- the hand-written one had already drifted out of order. */
const BulkOverrideGridFrame = ({ columnDefs, state, children }) => {
  const headers = columnDefs.filter(({ headerName }) => headerName);

  return (
    <div className='gridwrap'>
      <table id='tm'>
        <thead>
          <tr>
            <th className='c1'> </th>
            {headers.map(({ headerName }) => (
              <th key={headerName}>{headerName}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {state ? (
            <tr className='gsrow'>
              <td colSpan={headers.length + 1}>{state}</td>
            </tr>
          ) : (
            children
          )}
        </tbody>
      </table>
    </div>
  );
};

BulkOverrideGridFrame.propTypes = {
  columnDefs: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  state: PropTypes.node,
  children: PropTypes.node,
};

BulkOverrideGridFrame.defaultProps = {
  state: null,
  children: null,
};

BulkOverrideGridState.propTypes = {
  tone: PropTypes.string,
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
  body: PropTypes.string.isRequired,
  actionLabel: PropTypes.string,
  onAction: PropTypes.func,
};

BulkOverrideGridState.defaultProps = {
  tone: '',
  actionLabel: null,
  onAction: () => {},
};

const BulkOverrideTable = (props) => {
  const {
    rows,
    isReview,
    isProductChange,
    isUnfulfillment,
    isQuantityChange,
    isFillLaterSplit,
    isFillLaterMove,
    targetProductName,
    isLoading,
    hasScope,
    hasRowsBeforeFilters,
    loadError,
    onRetry,
    valueColumnLabel,
    valueOptions,
    placeholder,
    selectedIds,
    stagedValues,
    isRowChanged,
    isValuePickerReady,
    isQtyEditable,
    isQtyColumnVisible,
    onToggleRow,
    onToggleAll,
    onRowValueChange,
    onRowFieldChange,
    onClearFilters,
    onChangeDrug,
    gridRef,
  } = props;

  const isAll = rows.length > 0 && selectedIds.length === rows.length;
  const isSome = selectedIds.length > 0 && !isAll;

  /* The Fill dates cell stacks one line per split, so a row carrying three of them does not fit
   * the theme's 46px. Grown per row rather than globally: only the split does this, and only the
   * rows that were actually stamped. */
  const getRowHeight = useCallback(
    ({ data }) => {
      if (!isFillLaterSplit) return undefined;

      const lines = completeLines(stagedValues?.[data?.lineItemId]?.splits).length;

      return lines > 2 ? SCHEDULE_ROW_PADDING + lines * SCHEDULE_LINE_HEIGHT : undefined;
    },
    [isFillLaterSplit, stagedValues]
  );

  /* AG Grid measures height once per row and caches it, so a schedule stamped after the rows were
   * drawn leaves every row at its old height until the cache is dropped. */
  const scheduleDepth = useMemo(
    () =>
      !isFillLaterSplit
        ? 0
        : rows.reduce(
            (deepest, row) =>
              Math.max(deepest, completeLines(stagedValues?.[row.lineItemId]?.splits).length),
            0
          ),
    [isFillLaterSplit, rows, stagedValues]
  );

  useEffect(() => {
    gridRef?.current?.api?.resetRowHeights();
  }, [scheduleDepth, gridRef]);

  /* Layout inputs only -- never the selection or the staged values.
   *
   * isQtyColumnVisible is a layout input, not volatile state: it flips only when the quantity
   * filter goes from empty to set or back, at most twice in a session, and it flips on the same
   * change that replaces the row set anyway. Toggling the column through the grid's own
   * visibility API was tried first and rejected -- a hidden column leaves the display order, so
   * both setColumnsVisible and applyColumnState put it back at the far end of the row rather than
   * beside the quantity it belongs to. */
  const columnDefs = useMemo(() => {
    /* Selection-only: there is no value to set, so the row carries what the operator is deciding
     * on and nothing else. */
    if (isUnfulfillment) {
      return [
        CHECK_COLUMN,
        ORDER_ID_COLUMN,
        RX_COLUMN,
        DRUG_NAME_COLUMN,
        NDC_COLUMN,
        QTY_COLUMN,
        PARTNER_COLUMN,
        FACILITY_COLUMN,
      ];
    }

    /* The value column IS the new-quantity editor here, so NEW_QTY_COLUMN is not appended on top
     * of it — QTY_COLUMN_PAIRED carries the current quantity IMMEDIATELY beside it, struck through
     * once the row is staged, so the old and new values read as one before/after pair rather than
     * sitting at opposite ends of the row.
     *
     * No Current NDC column: this operation changes neither the drug nor its NDC, so the column
     * was a constant repeated down every row. The NDC advanced filter stays — narrowing by NDC is
     * still a way to pick which orders to act on. */
    if (isQuantityChange) {
      return [
        CHECK_COLUMN,
        ORDER_ID_COLUMN,
        RX_COLUMN,
        FACILITY_COLUMN,
        PARTNER_COLUMN,
        QTY_COLUMN_PAIRED,
        NEW_QTY_COLUMN,
      ];
    }

    if (isFillLaterMove) {
      return [
        CHECK_COLUMN,
        ORDER_ID_COLUMN,
        RX_COLUMN,
        PATIENT_NAME_COLUMN,
        FACILITY_COLUMN,
        PARTNER_COLUMN,
        QTY_COLUMN,
        MOVE_DATE_COLUMN,
      ];
    }

    /* Current quantity, what the original is left with, then what is split off -- read left to
     * right as the sum it is. No NDC column: a split changes neither the drug nor its NDC. */
    if (isFillLaterSplit) {
      return [
        CHECK_COLUMN,
        ORDER_ID_COLUMN,
        RX_COLUMN,
        PATIENT_NAME_COLUMN,
        FACILITY_COLUMN,
        PARTNER_COLUMN,
        { ...QTY_COLUMN_PAIRED, headerName: 'Quantity to dispense', width: 132, maxWidth: 150 },
        RETAINED_QTY_COLUMN,
        SPLIT_QTY_COLUMN,
        FILL_DATES_COLUMN,
      ];
    }

    return [
      CHECK_COLUMN,
      ORDER_ID_COLUMN,
      RX_COLUMN,
      ...(isReview
        ? [DRUG_COLUMN, PARTNER_COLUMN, QTY_COLUMN, FACILITY_COLUMN]
        : [
            FACILITY_COLUMN,
            ...(isProductChange
              ? [PARTNER_COLUMN, QTY_COLUMN_PAIRED, ...(isQtyColumnVisible ? [NEW_QTY_COLUMN] : [])]
              : [QTY_COLUMN, PARTNER_COLUMN]),
            isProductChange ? CURRENT_DRUG_COLUMN : CURRENT_NDC_COLUMN,
          ]),
      {
        headerName: valueColumnLabel,
        cellRenderer: ValueCell,
        flex: 1,
        minWidth: 200,
        sortable: false,
        autoHeight: isProductChange,
      },
    ];
  }, [
    isReview,
    isProductChange,
    isUnfulfillment,
    isQuantityChange,
    isFillLaterSplit,
    isFillLaterMove,
    isQtyColumnVisible,
    valueColumnLabel,
  ]);

  const gridState = useMemo(
    () => ({
      isAll,
      isSome,
      selectedIds,
      stagedValues,
      isRowChanged,
      valueOptions,
      placeholder,
      isValuePickerReady,
      isReview,
      isProductChange,
      isQuantityChange,
      targetProductName,
      isQtyEditable,
      onToggleRow,
      onToggleAll,
      onRowValueChange,
      onRowFieldChange,
    }),
    [
      isAll,
      isSome,
      selectedIds,
      stagedValues,
      isRowChanged,
      valueOptions,
      placeholder,
      isValuePickerReady,
      isReview,
      isProductChange,
      isQuantityChange,
      targetProductName,
      isQtyEditable,
      onToggleRow,
      onToggleAll,
      onRowValueChange,
      onRowFieldChange,
    ]
  );

  const rowClassRules = useMemo(
    () => ({
      'r-green': ({ data }) => isRowChanged(data),
      ck: ({ data }) => selectedIds.includes(data.lineItemId),
    }),
    [isRowChanged, selectedIds]
  );

  if (!hasScope) {
    return (
      <div className='gridwrap'>
        <div className='gridnote'>
          <b>Select a drug to get started</b>
          Its open orders load here.
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <BulkOverrideGridFrame
        columnDefs={columnDefs}
        state={
          <BulkOverrideGridState
            tone='warn'
            icon={WarnIcon}
            title='These orders could not be loaded'
            body='The request failed, so nothing is listed. This does not mean the drug has no open orders.'
            actionLabel='Try again'
            onAction={onRetry}
          />
        }
      />
    );
  }

  if (isLoading) {
    const skeletonCellCount = columnDefs.filter(({ headerName }) => headerName).length;

    return (
      <BulkOverrideGridFrame columnDefs={columnDefs}>
        {Array.from({ length: SKELETON_ROWS }, (unused, row) => (
          // eslint-disable-next-line react/no-array-index-key
          <tr className='skrow' aria-hidden='true' key={`sk${row}`}>
            <td className='c1'>
              <span className='sk' style={{ width: 17, height: 17, borderRadius: 3 }} />
            </td>
            {/* One cell per HEADER, not a fixed list plus a trailing wide one. The wide pill
                stands in for the value column, and quantity mode has none -- its New qty IS the
                last named column -- so the fixed shape emitted 8 cells against a 7-cell header
                and the placeholder rows ran wider than the navy header bar. Derived from
                columnDefs so every other mode (7 named columns) renders exactly as before:
                six narrow pills then one wide. */}
            {Array.from({ length: skeletonCellCount }, (ignored, cell) =>
              cell < SKELETON_WIDTHS.length ? (
                // eslint-disable-next-line react/no-array-index-key
                <td key={`${row}-n${cell}`}>
                  <span className='sk' style={{ width: SKELETON_WIDTHS[cell] }} />
                </td>
              ) : (
                // eslint-disable-next-line react/no-array-index-key
                <td key={`${row}-w${cell}`}>
                  <span className='sk' style={{ width: 250, height: 34, borderRadius: 6 }} />
                </td>
              )
            )}
          </tr>
        ))}
      </BulkOverrideGridFrame>
    );
  }

  if (!rows.length) {
    return (
      <BulkOverrideGridFrame
        columnDefs={columnDefs}
        state={
          hasRowsBeforeFilters ? (
            <BulkOverrideGridState
              icon={GridIcon}
              title='No orders match these filters'
              body='Every order is still here. Widen or clear the filters to bring them back.'
              actionLabel='Clear filters'
              onAction={onClearFilters}
            />
          ) : (
            <BulkOverrideGridState
              icon={ClipboardIcon}
              title={isReview ? 'No open orders to review' : 'No open orders on this drug'}
              body={
                isReview
                  ? 'Every open order is either already in review or has been dealt with.'
                  : 'There is nothing to change here right now. Pick a different drug to carry on.'
              }
              actionLabel={isReview ? null : 'Select another drug'}
              onAction={onChangeDrug}
            />
          )
        }
      />
    );
  }

  return (
    <div className='gridwrap' style={{ overflow: 'hidden' }}>
      <GridStateContext.Provider value={gridState}>
        <div style={{ height: '100%' }}>
          <AgGridReact
            ref={gridRef}
            theme={boTheme}
            rowData={rows}
            columnDefs={columnDefs}
            defaultColDef={boDefaultColDef}
            rowClassRules={rowClassRules}
            getRowId={({ data }) => data.lineItemId}
            getRowHeight={getRowHeight}
            enableCellTextSelection
            suppressCellFocus
            suppressMovableColumns={false}
          />
        </div>
      </GridStateContext.Provider>
    </div>
  );
};

BulkOverrideTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({})),
  isReview: PropTypes.bool,
  isProductChange: PropTypes.bool,
  isUnfulfillment: PropTypes.bool,
  isQuantityChange: PropTypes.bool,
  isFillLaterSplit: PropTypes.bool,
  isFillLaterMove: PropTypes.bool,
  targetProductName: PropTypes.string,
  isLoading: PropTypes.bool,
  hasScope: PropTypes.bool,
  hasRowsBeforeFilters: PropTypes.bool,
  loadError: PropTypes.bool,
  onRetry: PropTypes.func,
  valueColumnLabel: PropTypes.string.isRequired,
  valueOptions: PropTypes.arrayOf(PropTypes.shape({})),
  placeholder: PropTypes.string.isRequired,
  selectedIds: PropTypes.arrayOf(PropTypes.string),
  stagedValues: PropTypes.shape({}),
  isRowChanged: PropTypes.func.isRequired,
  isValuePickerReady: PropTypes.bool,
  isQtyEditable: PropTypes.bool,
  isQtyColumnVisible: PropTypes.bool,
  onToggleRow: PropTypes.func.isRequired,
  onToggleAll: PropTypes.func.isRequired,
  onRowValueChange: PropTypes.func.isRequired,
  onRowFieldChange: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
  onChangeDrug: PropTypes.func.isRequired,
  gridRef: PropTypes.shape({
    current: PropTypes.shape({ api: PropTypes.shape({ resetRowHeights: PropTypes.func }) }),
  }).isRequired,
};

BulkOverrideTable.defaultProps = {
  rows: [],
  isReview: false,
  isProductChange: false,
  isUnfulfillment: false,
  isQuantityChange: false,
  isFillLaterSplit: false,
  isFillLaterMove: false,
  targetProductName: null,
  isLoading: false,
  hasScope: true,
  hasRowsBeforeFilters: false,
  loadError: false,
  onRetry: () => {},
  valueOptions: [],
  selectedIds: [],
  stagedValues: {},
  isValuePickerReady: true,
  isQtyEditable: false,
  isQtyColumnVisible: false,
};

/* The capped select-all is hidden: operators did not read “Select [ All ]” as a control they
 * could type into. Nothing was removed -- flip this to true and the input, the limit state in
 * useBulkOverride and the sorted-order handling all come back as they were. */
const SHOW_SELECT_LIMIT = false;

export const BulkOverrideFooter = (props) => {
  const {
    hasScope,
    isCommitBlocked,
    isLoading,
    rowCount,
    selectedCount,
    stagedCount,
    hiddenStagedCount,
    isCommitting,
    selectLimit,
    selectTargetCount,
    onSelectLimitChange,
    onSelectAll,
    onClearSelection,
    onBack,
    onCommit,
  } = props;

  const isAllSelected = rowCount > 0 && selectedCount >= selectTargetCount;

  return (
    <div className='mfoot'>
      <span className='tally'>
        {isLoading && <span>Loading orders…</span>}
        {!isLoading && hasScope ? (
          <>
            {/* Only the two numbers that are not already on screen: how many orders there are,
                and how many a select-all will take. Selected shows in the apply bar and staged
                shows on the commit button, so repeating them here was noise. */}
            <span>
              <b>{rowCount}</b> {`order${rowCount === 1 ? '' : 's'}`}
            </span>
            {rowCount > 0 && SHOW_SELECT_LIMIT && (
              <span className='selcap'>
                <button
                  type='button'
                  className='rmall selall'
                  onClick={isAllSelected ? onClearSelection : onSelectAll}
                >
                  {isAllSelected ? 'Clear selection' : 'Select'}
                </button>
                <input
                  type='number'
                  min='1'
                  value={selectLimit}
                  placeholder='All'
                  aria-label='How many orders to select'
                  onChange={(event) =>
                    onSelectLimitChange(event.target.value.replace(/[^0-9]/g, ''))
                  }
                />
              </span>
            )}
            {rowCount > 0 && !SHOW_SELECT_LIMIT && (
              <button
                type='button'
                className='rmall selall'
                onClick={isAllSelected ? onClearSelection : onSelectAll}
              >
                {isAllSelected ? 'Clear selection' : `Select all ${rowCount}`}
              </button>
            )}
            {hiddenStagedCount > 0 && (
              <span className='selnote'>
                {hiddenStagedCount} staged change
                {hiddenStagedCount === 1 ? ' is' : 's are'} hidden by the current filter and will
                not be committed.
              </span>
            )}
          </>
        ) : null}
        {!isLoading && !hasScope && <span>No drug selected</span>}
      </span>

      <div className='sp' />
      <button type='button' className='btn' onClick={onBack}>
        Back
      </button>
      {/* isCommitBlocked carries the split's date requirement. Without it a run with rows staged
          but no date reaches the server and comes back as a per-row failure on every row. */}
      <button
        type='button'
        className='btn btn-b'
        disabled={!stagedCount || isCommitting || isCommitBlocked}
        onClick={onCommit}
      >
        <span>
          {stagedCount
            ? `Commit ${stagedCount} change${stagedCount === 1 ? '' : 's'}`
            : 'Commit changes'}
        </span>
      </button>
    </div>
  );
};

BulkOverrideFooter.propTypes = {
  hasScope: PropTypes.bool,
  isLoading: PropTypes.bool,
  rowCount: PropTypes.number,
  selectedCount: PropTypes.number,
  stagedCount: PropTypes.number,
  isCommitBlocked: PropTypes.bool,
  hiddenStagedCount: PropTypes.number,
  isCommitting: PropTypes.bool,
  selectLimit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectTargetCount: PropTypes.number,
  onSelectLimitChange: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  onClearSelection: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onCommit: PropTypes.func.isRequired,
};

BulkOverrideFooter.defaultProps = {
  isCommitBlocked: false,
  selectLimit: '',
  selectTargetCount: 0,
  hasScope: true,
  isLoading: false,
  rowCount: 0,
  selectedCount: 0,
  stagedCount: 0,
  hiddenStagedCount: 0,
  isCommitting: false,
};

/* ---------- BulkOverrideChooser : step 1: pick an action ---------- */

const Warning = ({ warning }) => (
  <div className={`hw-warn ${warning.level}`}>
    <b>
      {warning.level === 'low' ? <InfoIcon /> : <WarnIcon />}
      {warning.title}
    </b>
    <ul>
      {warning.items.map((item) => (
        <li key={item}>{renderMarks(item)}</li>
      ))}
    </ul>
  </div>
);

Warning.propTypes = {
  warning: PropTypes.shape({
    level: PropTypes.string,
    title: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};

const BulkOverrideHowItWorks = ({ howto }) => (
  <div className='ch-how'>
    <span className='hw-l'>How it works</span>

    {howto ? (
      <div className='hw-row'>
        {howto.steps.map(([title, detail], index) => (
          <div className='hw-s' key={title}>
            <i>{index + 1}</i>
            <div>
              <b>{title}</b>
              <span>{detail}</span>
            </div>
          </div>
        ))}
      </div>
    ) : (
      <div className='hw-idle'>
        <b>{BULK_OVERRIDE_IDLE_HOWTO.title}</b>
        {BULK_OVERRIDE_IDLE_HOWTO.body}
      </div>
    )}

    <Warning warning={howto ? howto.warning : BULK_OVERRIDE_IDLE_WARNING} />
  </div>
);

BulkOverrideHowItWorks.propTypes = {
  howto: PropTypes.shape({
    steps: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)),
    warning: PropTypes.shape({}),
  }),
};

BulkOverrideHowItWorks.defaultProps = { howto: null };

const REVIEW_INDEX = 2;

/* With one action left there is nothing to choose, so it starts chosen -- the operator lands on a
 * Continue they can press rather than a card they have to click first. Goes back to null the
 * moment a second action returns. */
const ONLY_ACTION = BULK_OVERRIDE_ACTIONS.length === 1 ? BULK_OVERRIDE_ACTIONS[0].operation : null;

const BulkOverrideChooser = ({ onClose, onContinue }) => {
  const [operation, setOperation] = useState(ONLY_ACTION);
  const [isUp, setIsUp] = useState(false);
  const optionsRef = useRef(null);

  useEffect(() => {
    setIsUp(true);
    const first = optionsRef.current?.querySelector('.opt');
    const timer = setTimeout(() => first?.focus(), 20);
    return () => clearTimeout(timer);
  }, []);

  const chosen = BULK_OVERRIDE_ACTIONS.find((action) => action.operation === operation);
  /* Fill later asks which kind before the order screen, so it is the one action with a third
   * step. Read off the live selection, so the count changes as the operator picks. */
  const stepCount = operation === bulkOverrideOperation.FILL_LATER_SPLIT ? 3 : 2;

  // arrows walk the list, 1-3 jump straight to one, Enter goes
  const handleKeyDown = (event) => {
    const options = [...optionsRef.current.querySelectorAll('.opt')];
    const at = options.indexOf(document.activeElement);

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : options.length - 1;
      const next = options[(at < 0 ? 0 : at + step) % options.length];
      next.focus();
      next.click();
      return;
    }

    if (event.key >= '1' && event.key <= '3') {
      const option = options[Number(event.key) - 1];
      if (!option) return;
      event.preventDefault();
      option.focus();
      option.click();
      return;
    }

    if (event.key === 'Enter' && operation) {
      event.preventDefault();
      onContinue(operation);
    }
  };

  return (
    <BulkOverridePortal>
      <div
        className={`veil0${isUp ? ' up' : ''}`}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) onClose();
        }}
        role='presentation'
      >
        <div className='chooser' role='dialog' aria-modal='true' aria-labelledby='chTitle'>
          <div className='ch-head'>
            <div style={{ flex: 1 }}>
              <span className='step'>{`Step 1 of ${stepCount}`}</span>
              <h3 id='chTitle'>Bulk update</h3>
              <p>Choose what you want to update. You’ll select the orders on the next step.</p>
            </div>
            <button type='button' className='x' onClick={onClose} aria-label='Close'>
              <CloseIcon />
            </button>
          </div>

          <div className='ch-main'>
            <div className='seclbl'>
              <GridIcon />
              <span>Select an action</span>
            </div>

            <div
              className='ch-body'
              role='radiogroup'
              aria-labelledby='chTitle'
              tabIndex={-1}
              ref={optionsRef}
              onKeyDown={handleKeyDown}
            >
              {BULK_OVERRIDE_ACTIONS.map((action, index) => {
                const Icon = ACTION_ICONS[action.icon];
                const isOn = action.operation === operation;

                return [
                  index === REVIEW_INDEX ? (
                    <div className='ch-sep' role='presentation' key='sep' />
                  ) : null,
                  <button
                    type='button'
                    className={`opt${isOn ? ' on' : ''}`}
                    role='radio'
                    aria-checked={isOn}
                    key={action.operation}
                    onClick={() => setOperation(action.operation)}
                  >
                    <span className='opt-ic'>
                      <Icon />
                    </span>
                    <span className='opt-t'>
                      <b>{action.title}</b>
                      <span>{action.description}</span>
                    </span>
                    <span className='opt-r'>
                      <i />
                    </span>
                  </button>,
                ];
              })}
            </div>

            <div className='ch-explain'>
              <BulkOverrideHowItWorks howto={operation ? BULK_OVERRIDE_HOWTO[operation] : null} />
            </div>
          </div>

          <div className='ch-foot'>
            <button type='button' className='btn' onClick={onClose}>
              Cancel
            </button>
            <button
              type='button'
              className='btn btn-b'
              id='chGo'
              disabled={!operation}
              onClick={() => onContinue(operation)}
            >
              {chosen ? chosen.continueLabel : 'Continue'}
            </button>
          </div>
        </div>
      </div>
    </BulkOverridePortal>
  );
};

BulkOverrideChooser.propTypes = {
  onClose: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
};

const BulkFillLaterModeChooser = ({ onBack, onContinue }) => {
  const [mode, setMode] = useState(null);
  const [isUp, setIsUp] = useState(false);
  const optionsRef = useRef(null);

  useEffect(() => {
    setIsUp(true);
    const first = optionsRef.current?.querySelector('.opt');
    const timer = setTimeout(() => first?.focus(), 20);
    return () => clearTimeout(timer);
  }, []);

  const chosen = FILL_LATER_MODES.find((entry) => entry.mode === mode);

  return (
    <BulkOverridePortal>
      <div
        className={`veil0${isUp ? ' up' : ''}`}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) onBack();
        }}
        role='presentation'
      >
        <div
          className='chooser'
          role='dialog'
          aria-modal='true'
          aria-labelledby='flModeTitle'
          style={{ maxWidth: 560, width: '100%' }}
        >
          <div className='ch-head'>
            <div style={{ flex: 1 }}>
              <span className='step'>Step 2 of 3</span>
              <h3 id='flModeTitle'>What kind of fill later?</h3>
              <p>Choose how the quantity moves. You&rsquo;ll select the orders on the next step.</p>
            </div>
            <button type='button' className='x' onClick={onBack} aria-label='Close'>
              <CloseIcon />
            </button>
          </div>

          <div className='ch-main'>
            <div className='seclbl'>
              <GridIcon />
              <span>Select a fill later type</span>
            </div>

            <div
              className='ch-body'
              role='radiogroup'
              aria-labelledby='flModeTitle'
              tabIndex={-1}
              ref={optionsRef}
              style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', width: '100%' }}
            >
              {FILL_LATER_MODES.map((entry) => {
                const Icon = ACTION_ICONS[entry.icon];
                const isOn = entry.mode === mode;

                return (
                  <button
                    type='button'
                    className={`opt${isOn ? ' on' : ''}`}
                    role='radio'
                    aria-checked={isOn}
                    key={entry.mode}
                    onClick={() => setMode(entry.mode)}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 14,
                      maxWidth: 'none',
                    }}
                  >
                    <span className='opt-ic'>
                      <Icon />
                    </span>
                    <span className='opt-t'>
                      <b>{entry.title}</b>
                      <span>{entry.description}</span>
                    </span>
                    <span className='opt-r' style={{ top: '50%', transform: 'translateY(-50%)' }}>
                      <i />
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className='ch-foot'>
            <button type='button' className='btn' onClick={onBack}>
              Back
            </button>
            <button
              type='button'
              className='btn btn-b'
              id='flModeGo'
              disabled={!mode}
              onClick={() => onContinue(mode)}
            >
              {chosen ? chosen.continueLabel : 'Continue'}
            </button>
          </div>
        </div>
      </div>
    </BulkOverridePortal>
  );
};

BulkFillLaterModeChooser.propTypes = {
  onBack: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
};

/* ---------- BulkOverrideConfirmDialog : the commit summary ---------- */

const ChangeLine = ({ group }) => (
  <div className='cf-change'>
    <span className={group.isMono ? 'mono' : undefined}>
      {group.from ? (
        <>
          {group.from}
          <em>&rarr;</em>
          <b>{group.to}</b>
        </>
      ) : (
        <b>{group.to}</b>
      )}
    </span>
    <small>
      {`${group.count} order${group.count === 1 ? '' : 's'}`}
      {group.note ? ` · ${group.note}` : ''}
    </small>
  </div>
);

ChangeLine.propTypes = {
  group: PropTypes.shape({
    from: PropTypes.string,
    to: PropTypes.string,
    count: PropTypes.number,
    note: PropTypes.string,
    isMono: PropTypes.bool,
  }).isRequired,
};

/* ---------- CommitProgressTracks : the two phases of a queued commit, side by side ----------
 *
 * A queued NDC/product commit is two runs the user cares about separately: writing the override,
 * then rebuilding each label so the printed NDC matches. They OVERLAP — core enqueues label
 * regeneration per chunk as that chunk's writes commit, not once at the end — so both tracks are
 * live at the same time and neither may wait for the other to finish before it starts moving.
 *
 * The two tracks deliberately count different things ("orders" vs "labels"): the differing unit is
 * what stops two side-by-side "X of 966" bars from reading as one duplicated progress.
 */

const TrackDoneIcon = () => (
  <Glyph size={13} box={16} stroke={2.2}>
    <path d='M3 8.5l4 4 6.5-7.5' />
  </Glyph>
);

const CommitProgressTracks = ({ stepOneLabel, progress, labelProgress, ndcOutcome }) => {
  const isStepOneDone = Boolean(ndcOutcome);

  const stepOneText = (() => {
    if (isStepOneDone) {
      return ndcOutcome.failed > 0
        ? `${ndcOutcome.succeeded} of ${ndcOutcome.total} — ${ndcOutcome.failed} failed`
        : `${ndcOutcome.total} of ${ndcOutcome.total} — done`;
    }

    if (progress.totalBatches === 0) return `Queued ${progress.total} orders…`;

    const failedNote = progress.failed > 0 ? ` · ${progress.failed} failed` : '';

    return `${progress.processedCount} of ${progress.total} orders${failedNote}`;
  })();

  /* Denominator: every submitted order until the run's completion event names the real number of
   * successes — only succeeded orders ever regenerate, so from then on the target snaps to it. */
  const labelTarget = ndcOutcome ? ndcOutcome.succeeded : labelProgress.total;
  const rebuilt = Math.min(labelProgress.completedCount, labelTarget);
  const isLabelWaiting = rebuilt === 0;
  const labelPercent = labelTarget > 0 ? Math.round((rebuilt / labelTarget) * 100) : 0;

  return (
    <div className='cf-tracks'>
      <div className={`cf-track${isStepOneDone ? ' done' : ''}`}>
        <div className='cf-track-head'>
          {isStepOneDone ? (
            <span className='cf-track-check' aria-hidden='true'>
              <TrackDoneIcon />
            </span>
          ) : (
            <span className='cf-track-spin' aria-hidden='true' />
          )}
          <span className='cf-track-name'>{`Step 1 · ${stepOneLabel}`}</span>
          <span className='cf-track-count'>{stepOneText}</span>
        </div>
        <div className='cf-prog-bar'>
          <div
            className='cf-prog-fill'
            style={{ width: `${isStepOneDone ? 100 : progress.percent}%` }}
            role='progressbar'
            aria-valuenow={isStepOneDone ? 100 : progress.percent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={stepOneLabel}
          />
        </div>
      </div>

      <div className={`cf-track${isLabelWaiting ? ' waiting' : ''}`}>
        <div className='cf-track-head'>
          {isLabelWaiting ? (
            <span className='cf-track-dot' aria-hidden='true' />
          ) : (
            <span className='cf-track-spin' aria-hidden='true' />
          )}
          <span className='cf-track-name'>Step 2 · Regenerate labels</span>
          <span className='cf-track-count'>
            {isLabelWaiting ? 'Waiting for updated orders…' : `${rebuilt} of ${labelTarget} labels`}
          </span>
        </div>
        <div className='cf-prog-bar'>
          <div
            className='cf-prog-fill'
            style={{ width: `${labelPercent}%` }}
            role='progressbar'
            aria-valuenow={labelPercent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label='Regenerating labels'
          />
        </div>
      </div>

      {isStepOneDone && (
        <p className='cf-track-hint'>
          The order changes are committed. Please wait while the remaining labels are regenerated —
          this can take a few minutes to complete.
        </p>
      )}
    </div>
  );
};

CommitProgressTracks.propTypes = {
  stepOneLabel: PropTypes.string.isRequired,
  progress: PropTypes.shape({
    percent: PropTypes.number,
    total: PropTypes.number,
    processedCount: PropTypes.number,
    failed: PropTypes.number,
    totalBatches: PropTypes.number,
  }).isRequired,
  labelProgress: PropTypes.shape({
    completedCount: PropTypes.number,
    total: PropTypes.number,
  }).isRequired,
  // The settled outcome of step 1. null while the override run is still going.
  ndcOutcome: PropTypes.shape({
    succeeded: PropTypes.number,
    failed: PropTypes.number,
    total: PropTypes.number,
  }),
};

CommitProgressTracks.defaultProps = {
  ndcOutcome: null,
};

// Exported for its test suite only (same pattern as BulkOverrideFooter below).
export const BulkOverrideConfirmDialog = (props) => {
  const {
    title,
    note,
    isCommitting,
    onClose,
    onConfirm,
    progress,
    labelProgress,
    ndcOutcome,
    stepOneLabel,
    workingSubtitle,
  } = props;
  /* Frozen on open. Committing clears the staging and refetches the list while this is still
   * up, so reading it live made the summary count itself down to “0 orders will be
   * updated” underneath the spinner. Nothing behind a modal can change it anyway. */
  const [summary] = useState(() => {
    const { count, scope, sections, untouchedCount, selectedCount } = props;

    return { count, scope, sections, untouchedCount, selectedCount };
  });
  const { count, scope, sections, untouchedCount, selectedCount } = summary;

  /* Closing during the label phase is a decision, not a click: the operator leaves without
   * confirmation that every label was rebuilt, so the Close button arms this warning first.
   * Reset is unnecessary — the dialog unmounts on close, and the phase settling closes it. */
  const [isCloseWarningOpen, setIsCloseWarningOpen] = useState(false);

  return (
    <BulkOverridePortal>
      <div
        className='veil2 up'
        role='presentation'
        onMouseDown={(event) => {
          /* No silent close while the run is processing — submitting (isCommitting), queued and
           * tracking (progress.isTracking), or rebuilding labels (ndcOutcome): the first two
           * match the disabled Go back button, and the label phase must go through the Close
           * button's warning below rather than a stray backdrop click. */
          const isProcessing = isCommitting || progress?.isTracking || Boolean(ndcOutcome);

          if (event.target === event.currentTarget && !isProcessing) onClose();
        }}
      >
        <div className='confirm' role='dialog' aria-modal='true' aria-labelledby='cfTitle'>
          <div className='cf-head'>
            <div className='cf-ic'>
              <ConfirmWarnIcon />
            </div>
            <div>
              <h3 id='cfTitle'>{title}</h3>
              <p>
                <b>{count}</b>
                {/* "Nothing has been written yet" stops being true the moment the first chunk
                 * commits, so a running two-phase commit gets its own line. */}
                {(progress?.isTracking || ndcOutcome) && workingSubtitle
                  ? ` order${count === 1 ? '' : 's'} — ${workingSubtitle}.`
                  : ` order${count === 1 ? '' : 's'} will be updated. Nothing has been written yet.`}
              </p>
            </div>
          </div>

          <div className='cf-body'>
            {scope.map((row) => (
              <div className='cf-row' key={row.label}>
                <span>{row.label}</span>
                <b>{row.value}</b>
              </div>
            ))}

            {sections.map((section) => (
              <div className='cf-sect' key={section.label}>
                <i>{section.label}</i>
                <div className='cf-list'>
                  {section.groups.map((group) => (
                    <ChangeLine group={group} key={`${group.from || 0}-${group.to}`} />
                  ))}
                </div>
              </div>
            ))}

            {untouchedCount > 0 && (
              <p className='cf-left'>
                {`${untouchedCount} of the ${selectedCount} orders you selected have nothing staged, ` +
                  'and are left exactly as they are.'}
              </p>
            )}

            <p className='cf-note'>{note}</p>

            {/* Only shown for a queued run. Below the server's threshold the commit finishes inside
             * the request and the button's own spinner already covers it — a bar that appears and
             * completes in one frame is worse than no bar.
             *
             * Operations that rebuild labels (NDC/product) get the two-phase tracks; the review
             * operation changes no label and keeps the single bar. */}
            {labelProgress
              ? (progress?.isTracking || Boolean(ndcOutcome)) && (
                  <CommitProgressTracks
                    stepOneLabel={stepOneLabel}
                    progress={progress}
                    labelProgress={labelProgress}
                    ndcOutcome={ndcOutcome}
                  />
                )
              : progress?.isTracking && (
                  <div className='cf-prog'>
                    <div className='cf-prog-bar'>
                      <div
                        className='cf-prog-fill'
                        style={{ width: `${progress.percent}%` }}
                        role='progressbar'
                        aria-valuenow={progress.percent}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label='Applying NDC overrides'
                      />
                    </div>
                    <span className='cf-prog-text'>
                      {progress.totalBatches > 0
                        ? `${progress.processedCount} of ${progress.total} orders updated`
                        : `Queued ${progress.total} orders…`}
                      {progress.failed > 0 ? ` · ${progress.failed} failed` : ''}
                    </span>
                  </div>
                )}
          </div>

          {/* The Close-during-regeneration warning. Rendered above the footer so the flex row
           * of buttons keeps its layout; role=alert so it is announced when it appears. */}
          {ndcOutcome && isCloseWarningOpen && (
            <p className='cf-track-hint' role='alert'>
              Are you sure you want to close the progress screen? Labels are still being regenerated
              — if you close now you will not see confirmation that every label was rebuilt. You
              will still get a notification when the run finishes.
            </p>
          )}

          <div className='cf-foot'>
            {/* Once the override run has settled there is nothing left to confirm or go back to.
             * But the labels are still rebuilding, and the operator's job is to see them finish —
             * the dialog closes ITSELF when they do — so Close first arms a warning rather than
             * leaving silently. Closing anyway is still allowed: the regeneration continues
             * server-side either way, and the completion snackbar still reports the outcome. */}
            {ndcOutcome && isCloseWarningOpen && (
              <>
                <button type='button' className='btn' onClick={() => setIsCloseWarningOpen(false)}>
                  Keep waiting
                </button>
                <button type='button' className='btn btn-b' onClick={onClose}>
                  Close anyway
                </button>
              </>
            )}
            {ndcOutcome && !isCloseWarningOpen && (
              <button
                type='button'
                className='btn btn-b'
                onClick={() => setIsCloseWarningOpen(true)}
              >
                Close
              </button>
            )}
            {!ndcOutcome && (
              <>
                <button
                  type='button'
                  className='btn'
                  onClick={onClose}
                  disabled={isCommitting || progress?.isTracking}
                >
                  Go back
                </button>
                {/* labelProgress too: while a previous run's label phase is still tracked, a
                 * re-confirm would feed a second run into a wait armed for the first one. */}
                <button
                  type='button'
                  className='btn btn-b'
                  onClick={onConfirm}
                  disabled={isCommitting || progress?.isTracking || labelProgress?.isTracking}
                >
                  {(isCommitting || progress?.isTracking) && (
                    <span className='cf-spin' aria-hidden='true' />
                  )}
                  {isCommitting || progress?.isTracking
                    ? 'Committing…'
                    : `Yes, commit ${count} change${count === 1 ? '' : 's'}`}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </BulkOverridePortal>
  );
};

BulkOverrideConfirmDialog.propTypes = {
  title: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  scope: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, value: PropTypes.string })),
  sections: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, groups: PropTypes.arrayOf(PropTypes.shape({})) })
  ),
  untouchedCount: PropTypes.number,
  selectedCount: PropTypes.number,
  note: PropTypes.string.isRequired,
  isCommitting: PropTypes.bool,
  // Realtime progress for a queued run. Absent on the synchronous path.
  progress: PropTypes.shape({
    isTracking: PropTypes.bool,
    percent: PropTypes.number,
    total: PropTypes.number,
    processedCount: PropTypes.number,
    failed: PropTypes.number,
    totalBatches: PropTypes.number,
  }),
  /* Realtime label-regeneration progress. Also gates re-confirming while a previous run's
   * label phase is still tracked. Absent for operations that never rebuild labels
   * (needs-review), which keeps the single-bar rendering for those. */
  labelProgress: PropTypes.shape({
    completedCount: PropTypes.number,
    total: PropTypes.number,
    isTracking: PropTypes.bool,
  }),
  // Settled outcome of the override run; while labels are still rebuilding it keeps step 1's
  // done-state on screen and switches the footer to Close.
  ndcOutcome: PropTypes.shape({
    succeeded: PropTypes.number,
    failed: PropTypes.number,
    total: PropTypes.number,
  }),
  stepOneLabel: PropTypes.string,
  workingSubtitle: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

BulkOverrideConfirmDialog.defaultProps = {
  isCommitting: false,
  progress: null,
  labelProgress: null,
  ndcOutcome: null,
  stepOneLabel: 'Update orders',
  workingSubtitle: null,
  scope: [],
  sections: [],
  untouchedCount: 0,
  selectedCount: 0,
};
/* ---------- BulkOverrideDiscardDialog : closing with work staged ---------- */

const BulkOverrideDiscardDialog = (props) => {
  const { count, onCancel, onDiscard } = props;
  const staged = `${count} staged change${count === 1 ? '' : 's'}`;

  return (
    <BulkOverridePortal>
      <div
        className='veil2 up'
        role='presentation'
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) onCancel();
        }}
      >
        <div className='confirm' role='dialog' aria-modal='true' aria-labelledby='dcTitle'>
          <div className='cf-head'>
            <div className='cf-ic'>
              <ConfirmWarnIcon />
            </div>
            <div>
              <h3 id='dcTitle'>{`Discard ${staged}?`}</h3>
              <p>Nothing has been written yet.</p>
            </div>
          </div>

          <div className='cf-body'>
            <p className='cf-note'>
              Closing clears everything you have staged, along with your filters and selection.
            </p>
          </div>

          <div className='cf-foot'>
            <button type='button' className='btn' onClick={onCancel}>
              Keep editing
            </button>
            <button type='button' className='btn btn-b' onClick={onDiscard}>
              {`Discard ${staged}`}
            </button>
          </div>
        </div>
      </div>
    </BulkOverridePortal>
  );
};

BulkOverrideDiscardDialog.propTypes = {
  count: PropTypes.number.isRequired,
  onCancel: PropTypes.func.isRequired,
  onDiscard: PropTypes.func.isRequired,
};
/* ---------- BulkOverrideDialog : step 2: header, shell, wiring ---------- */

const BulkOverrideHeader = (props) => {
  const { actionTitle, title, productId, lockLabel, stepCount, onBack, onReset, onClose } = props;

  return (
    <div className='mhead'>
      <div className='mhic' aria-hidden='true'>
        <ReviewHeadIcon />
      </div>
      <div style={{ flex: 1 }}>
        <span className='step'>
          {actionTitle} <span className='s2'>{`· Step ${stepCount} of ${stepCount}`}</span>
        </span>
        <h2>{title}</h2>
        <p className='mmeta'>
          {productId && (
            <>
              <span>
                Product ID <b className='mono'>{productId}</b>
              </span>
              <span className='sep'>|</span>
            </>
          )}
          <span className='lk'>
            <LockIcon />
            {lockLabel}
          </span>
        </p>
      </div>
      <button type='button' className='back' onClick={onBack}>
        <BackIcon /> Back
      </button>
      <button type='button' className='btn' onClick={onReset}>
        Reset changes
      </button>
      <button type='button' className='x' onClick={onClose} aria-label='Close'>
        <CloseIcon />
      </button>
    </div>
  );
};

BulkOverrideHeader.propTypes = {
  actionTitle: PropTypes.string.isRequired,
  stepCount: PropTypes.number,
  title: PropTypes.string.isRequired,
  productId: PropTypes.string,
  lockLabel: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

BulkOverrideHeader.defaultProps = {
  productId: null,
  stepCount: 2,
};

const MODE = {
  [bulkOverrideOperation.NDC_UPDATE]: 'ndc',
  [bulkOverrideOperation.PRODUCT_ID_UPDATE]: 'product',
  [bulkOverrideOperation.NEEDS_REVIEW_UPDATE]: 'review',
  [bulkOverrideOperation.UNFULFILLMENT_UPDATE]: 'unfulfillment',
  [bulkOverrideOperation.QUANTITY_UPDATE]: 'quantity',
};

const CONFIRM_TITLE = {
  [bulkOverrideOperation.NDC_UPDATE]: 'Commit NDC changes?',
  [bulkOverrideOperation.PRODUCT_ID_UPDATE]: 'Move these orders to a different product?',
  [bulkOverrideOperation.NEEDS_REVIEW_UPDATE]: 'Send these orders to review?',
  [bulkOverrideOperation.UNFULFILLMENT_UPDATE]: 'Return these orders to product fulfillment?',
  [bulkOverrideOperation.QUANTITY_UPDATE]: 'Commit quantity changes?',
  [bulkOverrideOperation.FILL_LATER_SPLIT]: 'Schedule these fill laters?',
  [bulkOverrideOperation.FILL_LATER_MOVE]: 'Move these items to fill later?',
};

/* What the commit actually did, per operation -- “updated” is true of an NDC change but
 * says nothing useful about the other two, which move the order somewhere else. */
const COMMITTED_VERB = {
  [bulkOverrideOperation.NDC_UPDATE]: 'updated',
  [bulkOverrideOperation.PRODUCT_ID_UPDATE]: 'moved to the new product',
  [bulkOverrideOperation.NEEDS_REVIEW_UPDATE]: 'sent to review',
  [bulkOverrideOperation.UNFULFILLMENT_UPDATE]: 'returned to product fulfillment',
  [bulkOverrideOperation.QUANTITY_UPDATE]: 'updated',
  [bulkOverrideOperation.FILL_LATER_SPLIT]: 'scheduled as fill laters',
  [bulkOverrideOperation.FILL_LATER_MOVE]: 'moved to fill later',
};

const CONFIRM_NOTE = {
  [bulkOverrideOperation.NDC_UPDATE]:
    'Only unfilled orders are affected — nothing already dispensed changes. There is no bulk ' +
    'undo: reversing it means running another change over the same orders.',
  [bulkOverrideOperation.PRODUCT_ID_UPDATE]:
    'Each order moves to the new product and gets a new NDC override; the previous one is deactivated. These orders leave the current drug’s queue.',
  [bulkOverrideOperation.NEEDS_REVIEW_UPDATE]:
    'These orders are tagged for review and leave the bulk queue. Nothing about the drug, NDC or quantity changes. They return once the review is cleared.',
  [bulkOverrideOperation.UNFULFILLMENT_UPDATE]:
    'Each order goes back to OPEN and its line item returns to product fulfillment; the lot reservations it held are released. There is no bulk redo — re-filling means going through fulfillment again.',
  [bulkOverrideOperation.QUANTITY_UPDATE]:
    'Only unfilled orders are affected — nothing already dispensed changes. The drug and NDC stay as they are. Any add-ons that scale with quantity are recalculated, and every label is rebuilt.',
  [bulkOverrideOperation.FILL_LATER_SPLIT]:
    'Nothing is ordered now. The quantity you split off is scheduled, and its order is created on the date you picked once that original has shipped; each original keeps the rest and carries on as normal. Scheduled fill laters can be rescheduled or cancelled from the Fill Later queue.',
  [bulkOverrideOperation.FILL_LATER_MOVE]:
    'Nothing is ordered now. The whole item leaves its order — nothing is left behind to fill — and is held as a scheduled fill later until the date you picked. When every item on an order moves, that order itself is held. Scheduled fill laters can be rescheduled or cancelled from the Fill Later queue.',
};

/* The two-phase commit tracks only exist for operations that rebuild labels. Needs-review tags
 * the order and touches no label, so it keeps the single progress bar. */
const STEP_ONE_LABEL = {
  [bulkOverrideOperation.NDC_UPDATE]: 'Update order NDCs',
  [bulkOverrideOperation.PRODUCT_ID_UPDATE]: 'Move orders to the new product',
  [bulkOverrideOperation.QUANTITY_UPDATE]: 'Update order quantities',
  [bulkOverrideOperation.FILL_LATER_SPLIT]: 'Schedule the fill laters',
  [bulkOverrideOperation.FILL_LATER_MOVE]: 'Move the items to fill later',
};

/* Replaces "Nothing has been written yet" once the run starts. "and", not "then": core enqueues
 * label regeneration per chunk as each chunk commits, so the phases overlap. */
const WORKING_SUBTITLE = {
  [bulkOverrideOperation.NDC_UPDATE]: 'updating NDCs and rebuilding labels',
  [bulkOverrideOperation.PRODUCT_ID_UPDATE]: 'moving orders and rebuilding labels',
  /* No label phase, but every unfulfillment run is queued regardless of size, so the headline
   * would otherwise read "nothing has been written yet" for the whole of a run that is writing. */
  [bulkOverrideOperation.UNFULFILLMENT_UPDATE]: 'returning orders to product fulfillment',
  [bulkOverrideOperation.QUANTITY_UPDATE]: 'updating quantities and rebuilding labels',
  [bulkOverrideOperation.FILL_LATER_SPLIT]: 'scheduling fill laters and rebuilding labels',
  [bulkOverrideOperation.FILL_LATER_MOVE]: 'moving items to fill later and rebuilding labels',
};

/**
 * The one snackbar a two-phase queued run ends with.
 *
 * `failedLabels` is only known when the server's own completion event settled the run — it counts
 * labels that genuinely failed to rebuild. Without it, `rebuilt` is just what the socket showed
 * us, and anything short of the succeeded count is reported as "still rebuilding" rather than as
 * failed: the row keeps its regenerating tag until the label actually lands, and calling those
 * failures without a server count would be a guess.
 */
const labelPhaseOutcomeSnackbar = ({ ndc, rebuilt, failedLabels, operation }) => {
  const shortfall = Math.max(ndc.succeeded - rebuilt, 0);
  const isAuthoritative = failedLabels !== undefined;

  const orders =
    ndc.failed > 0
      ? `${ndc.succeeded} of ${ndc.total} orders ${COMMITTED_VERB[operation]} · ${ndc.failed} failed.`
      : `${ndc.total} order${ndc.total === 1 ? '' : 's'} ${COMMITTED_VERB[operation]}.`;

  const labels = (() => {
    if (isAuthoritative && failedLabels > 0) {
      return `${rebuilt} label${rebuilt === 1 ? '' : 's'} rebuilt · ${failedLabels} could not be rebuilt.`;
    }

    if (!isAuthoritative && shortfall > 0) {
      return `${shortfall} label${shortfall === 1 ? ' is' : 's are'} still rebuilding in the background.`;
    }

    return `${rebuilt} label${rebuilt === 1 ? '' : 's'} rebuilt.`;
  })();

  const variant = (() => {
    if (ndc.failed > 0 || (isAuthoritative && failedLabels > 0)) return 'warning';

    return !isAuthoritative && shortfall > 0 ? 'info' : 'success';
  })();

  return {
    message: `${orders} ${labels}`,
    variant,
    autoHideDuration: variant === 'success' ? 5000 : 10000,
  };
};

const reasonOptions = map(BULK_OVERRIDE_REVIEW_REASONS, (label, value) => ({ value, label })).sort(
  (first, second) => first.label.localeCompare(second.label, undefined, { sensitivity: 'base' })
);

const BulkOverrideDialog = ({ operation, fillLaterMode, onClose, onBack }) => {
  const action = findBulkOverrideAction(operation);
  /* Derived here rather than taken off the hook: useBulkOverride sits on the repo's max-lines
   * limit, and this file is already exempt from it. Mode-aware, so the split-only branches
   * switch off for a move - both modes ride on the same operation.
   *
   * Declared at the top because the copy maps are read from the progress callbacks far above
   * where the rest of the derived state is assembled. */
  const { isFillLaterSplit, isFillLaterMove } = fillLaterModeFlags(operation, fillLaterMode);
  /* Every copy map is keyed by operation, and a move rides on FILL_LATER_SPLIT, so without this
   * they would all read as a split. */
  const copyKey = isFillLaterMove ? bulkOverrideOperation.FILL_LATER_MOVE : operation;
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isDiscardOpen, setIsDiscardOpen] = useState(false);

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const gridRef = useRef(null);
  const currentTenantId = useSelector(({ auth }) => auth?.currentTenantId);
  const facilities = useSelector(({ pharmacy }) => pharmacy[currentTenantId]);
  const partners = useSelector(({ partner }) => partner[currentTenantId]);

  // partners are loaded by the Shipments module, not by Work Queues, so this
  // screen has to ask for them itself the first time it opens
  useEffect(() => {
    if (!partners) dispatch(getPartners());
  }, [partners, dispatch]);

  /* Realtime progress for a queued run.
   *
   * onBackgroundComplete covers the case where the run finishes after this dialog is gone — the
   * user closed it, or refreshed. Nothing is persisted server-side, so without this the outcome of
   * their own action would never reach them. */
  const progress = useNdcOverrideProgress({
    /* Copy says "bulk update", not "bulk NDC update": the event carries no operation type, and
     * all three operations land here. The hook has already summed a chunked run's batches into
     * this one call. */
    onBackgroundComplete: ({ total, failed }) => {
      enqueueSnackbar(
        failed > 0
          ? `Bulk update finished: ${total - failed} of ${total} orders updated, ${failed} failed.`
          : `Bulk update finished: ${total} order${total === 1 ? '' : 's'} updated.`,
        { variant: failed > 0 ? 'warning' : 'success', autoHideDuration: 10000 }
      );
    },
  });

  /* Phase 2 of a queued NDC/product commit: the labels core rebuilds so the printed NDC matches.
   * Core enqueues that regeneration per chunk as each chunk's writes commit, and every rebuilt
   * label reaches us as a labels_regenerated socket event carrying its line item ids — the same
   * events the lot/NDC review flow already counts. Started alongside the override tracker in
   * onQueued below, so the two bars genuinely run in parallel.
   *
   * 5 minutes, not the hook's 15-minute default: with the poll fallback below, a run that goes
   * silent settles from the LABEL_REGENERATING row tag within a poll cycle or two, so the
   * timeout is a last resort rather than the only exit — and a quarter-hour frozen dialog is
   * a hang, not a fallback. Matches useCommittedLabelRegeneration's judgement. */
  const hasLabelPhase = Boolean(STEP_ONE_LABEL[copyKey]);
  const labelProgress = useLabelRegenerationProgress({ queuedTimeoutMs: 300000 });

  /* The override run's settled outcome — step 1's done-state while step 2 keeps going. Mirrored
   * in a ref because the label tracker's callbacks were registered before this outcome existed
   * and would otherwise close over null forever. */
  const [ndcOutcome, setNdcOutcome] = useState(null);
  const ndcOutcomeRef = useRef(null);
  /* True once the label phase has been settled. The settle is reachable from three sides — the
   * completed-count effect, the label tracker's own timeout, and the override run timing out —
   * and must run exactly once. Starts true so a stray socket event settles nothing. */
  const labelsSettledRef = useRef(true);
  /* The label tracker is started at most ONCE per run: a chunked commit starts it before its
   * first submit (early chunks regenerate labels while later chunks are still on the wire, and
   * events that arrive before tracking starts are dropped, not replayed), and onQueued starts it
   * for the single-request run. Restarting would reset the completed-id set mid-run. */
  const labelTrackingStartedRef = useRef(false);
  /* A partial run — some chunks accepted, then a submit failed — must NOT settle like a whole
   * one: the un-submitted rows are still staged as the operator's retry record, and the full
   * settle would wipe them. When true, the trackers reload the grid in place instead. */
  const isPartialRunRef = useRef(false);
  /* True from a chunked commit's beginRun until its run ends — how onQueued knows to finishRun
   * an already-live tracker rather than start a fresh one, and what the disarm in onConfirm
   * checks when a run that armed early never queued anything. */
  const progressRunBegunRef = useRef(false);

  /* onQueued needs bo.retry() to refresh the grid once the run settles, but it is passed INTO the
   * hook that produces `bo` — so it cannot close over `bo` directly without depending on the
   * const being initialised by the time the callback fires. That happens to hold today (the
   * callback only ever runs after an awaited mutation), but it would break the moment onQueued were
   * called synchronously from the hook body. A ref assigned after construction keeps it safe
   * regardless of when the callback runs. */
  const reloadRowsRef = useRef(null);
  /* Same declaration-order dance as reloadRowsRef: the queued-run callbacks are built before the
   * hook they need to call back into. */
  const settleRef = useRef(null);

  /* Ends the label phase: closes the dialog, resets both trackers and reports the whole run in
   * one snackbar. `rebuilt` is how many labels the socket showed us by then; `failedLabels` is
   * present only when the server's completion event settled the run and is the authoritative
   * count of labels that failed to rebuild. */
  const finishLabelPhase = useCallback(
    (rebuilt, failedLabels) => {
      if (labelsSettledRef.current) return;
      labelsSettledRef.current = true;

      const ndc = ndcOutcomeRef.current;

      ndcOutcomeRef.current = null;
      setNdcOutcome(null);
      setIsConfirmOpen(false);
      progress.reset();
      labelProgress.reset();
      /* The run's real ending, so it leaves the screen exactly as a synchronous commit does --
       * filters and drug dropped, or reloaded in place for review, which also picks up the
       * regenerating tags that cleared as labels landed. UNLESS the run was partial: then the
       * un-submitted rows are still staged as the retry record and the full settle would wipe
       * them, so the grid reloads in place around them instead. */
      const wasPartial = isPartialRunRef.current;

      labelTrackingStartedRef.current = false;
      progressRunBegunRef.current = false;
      isPartialRunRef.current = false;

      if (wasPartial) reloadRowsRef.current?.();
      else settleRef.current?.();

      // The override run never settled — its own timeout already told the user what happened.
      if (!ndc) return;

      const { message, variant, autoHideDuration } = labelPhaseOutcomeSnackbar({
        ndc,
        rebuilt,
        failedLabels,
        operation: copyKey,
      });

      enqueueSnackbar(message, { variant, autoHideDuration });
    },
    [enqueueSnackbar, labelProgress, copyKey, progress]
  );

  /* The ids the label phase is tracking — what the poll fallback below asks the row tag about. */
  const trackedLabelIdsRef = useRef([]);

  /* Also referenced from finishLabelPhase above via ref-reads only, so the callback's deps stay
   * honest: everything here is a ref, stable across renders. */
  const startLabelTracking = (lineItemIds) => {
    if (labelTrackingStartedRef.current || !hasLabelPhase || !lineItemIds?.length) return false;

    labelTrackingStartedRef.current = true;
    labelsSettledRef.current = false;
    trackedLabelIdsRef.current = lineItemIds;
    labelProgress.startTracking({
      requestLineItemIds: lineItemIds,
      /* Already on the queue — the commit mutation said so (or is about to say so for a chunked
       * run). Without this the tracker arms its short synchronous window for work that is going
       * through SQS. */
      queued: true,
      /* A run can be several server-side batches, each with its own completion event; the first
       * one must not settle the phase. How many to expect arrives later, from the override run's
       * outcome, via setExpectedCompletions. */
      multiRun: true,
      /* onComplete is the authoritative ending: core publishes LabelsRegenerationCompleted per
       * batch that succeeded at something, with a real count of labels that failed to rebuild.
       * The completed-count effect above and onTimeout remain the fallbacks for a silent socket
       * or a run whose completion events never fire. */
      onComplete: ({ completed, failed }) => finishLabelPhase(completed, failed ?? 0),
      onTimeout: ({ completed }) => finishLabelPhase(completed),
    });

    return true;
  };

  /* The label phase's normal ending: the socket has shown a rebuilt label for every order the
   * server said succeeded. The count is the authority on this path — core enqueues the
   * regeneration without a user context, so no completion event ever arrives to settle it. */
  useEffect(() => {
    if (!ndcOutcome || labelsSettledRef.current) return;

    if (labelProgress.completedCount >= ndcOutcome.succeeded) {
      finishLabelPhase(labelProgress.completedCount);
    }
  }, [ndcOutcome, labelProgress.completedCount, finishLabelPhase]);

  /* The label phase's stall recovery. A regeneration that stalls server-side (a poisoned SQS
   * message blocking its lane, a consumer killed mid-render) stops emitting events entirely —
   * the bar freezes and, before this, the only exit was the full queued timeout. The row-tag
   * poll settles the phase with the OBSERVED rebuilt count instead. Runs only while step 2 is
   * on screen: finishLabelPhase clears ndcOutcome, which unwinds this effect and stops it. */
  const labelPollFallback = useLabelRegenPollFallback({
    getSocketCompletedCount: labelProgress.getCompletedCount,
    onAllClear: () => finishLabelPhase(labelProgress.getCompletedCount()),
  });

  useEffect(() => {
    if (!ndcOutcome || labelsSettledRef.current) return undefined;

    labelPollFallback.start(trackedLabelIdsRef.current);

    return labelPollFallback.stop;
  }, [ndcOutcome, labelPollFallback]);

  /* The queued-run callbacks, shared by the two ways a run starts: beginRun before a chunked
   * commit's first submit, startTracking when a single-request commit resolves QUEUED.
   * `isTrackingLabels` is decided the moment the label tracker starts, so it is captured per
   * run rather than read from the ref — the ref describes whatever run is CURRENT, and these
   * callbacks may fire after a later run has claimed it. */
  const buildOverrideRunCallbacks = ({ isTrackingLabels }) => ({
    onComplete: ({ succeeded, failed, total, batchesWithSuccess }) => {
      /* Nothing to wait for — the operation rebuilds no labels, or nothing succeeded so
       * nothing will regenerate. Settle everything here, exactly as before. */
      if (!isTrackingLabels || succeeded === 0) {
        labelsSettledRef.current = true;
        setIsConfirmOpen(false);
        progress.reset();
        labelProgress.reset();

        enqueueSnackbar(
          failed > 0
            ? `${succeeded} of ${total} orders updated. ${failed} could not be updated.`
            : `${total} order${total === 1 ? '' : 's'} ${COMMITTED_VERB[copyKey]}.`,
          {
            variant: failed > 0 ? 'warning' : 'success',
            autoHideDuration: failed > 0 ? 10000 : 5000,
          }
        );

        /* Partial runs keep the un-submitted rows staged as the retry record, so the full
         * settle (which wipes all staging) must not run for them. */
        const wasPartial = isPartialRunRef.current;

        labelTrackingStartedRef.current = false;
        progressRunBegunRef.current = false;
        isPartialRunRef.current = false;

        if (wasPartial) reloadRowsRef.current?.();
        else settleRef.current?.();

        return;
      }

      /* Step 1 settles on screen; the dialog stays open for step 2. The ref is what the
       * label tracker's callbacks and the settle read — they were registered before this
       * outcome existed. */
      const outcome = { succeeded, failed, total };

      ndcOutcomeRef.current = outcome;
      setNdcOutcome(outcome);

      /* The label phase can now be told how many completion events make it whole: one per
       * batch that wrote anything — core publishes none for a batch with zero successes. Zero
       * is deliberately NOT handed over: successes from a synchronous chunk regenerate labels
       * without any completion event, and settling on "0 expected" would announce 0 labels
       * rebuilt while they are still landing. The completed-count effect covers that case. */
      if (batchesWithSuccess > 0) {
        labelProgress.setExpectedCompletions(batchesWithSuccess);
      }
    },
    /* No server-side progress record exists, so a run whose chunks end up in the DLQ never
     * reports completion. This is the only thing that closes the dialog in that case. The
     * label phase dies with it: without a succeeded count there is no target to settle
     * against, and the row tags cover any labels that do land later. */
    onTimeout: ({ succeeded, failed, total }) => {
      labelsSettledRef.current = true;
      labelTrackingStartedRef.current = false;
      progressRunBegunRef.current = false;
      isPartialRunRef.current = false;
      setIsConfirmOpen(false);
      progress.reset();
      labelProgress.reset();

      enqueueSnackbar(
        `Still working: ${succeeded + failed} of ${total} orders processed so far. ` +
          'Refresh the queue in a few minutes to see the rest.',
        { variant: 'info', autoHideDuration: 10000 }
      );

      reloadRowsRef.current?.();
    },
  });

  const bo = useBulkOverride({
    open: true,
    operation,
    isFillLaterMove,
    /* Before the FIRST chunk of a multi-chunk commit is on the wire. Both trackers must be
     * listening from here: core publishes a batch's queued event before that batch's own
     * mutation resolves, and an early chunk's labels regenerate while later chunks are still
     * submitting. Events that arrive before tracking starts are dropped, not replayed. */
    onChunkedCommitStart: ({ lineItemIds }) => {
      isPartialRunRef.current = false;

      const isTrackingLabels = startLabelTracking(lineItemIds);

      progressRunBegunRef.current = true;
      progress.beginRun(buildOverrideRunCallbacks({ isTrackingLabels }));
    },
    /* Each queued chunk registers the moment its mutation resolves — its first socket events
     * are already in flight, buffered by the tracker until this call claims them. */
    onBatchAccepted: (batch) => progress.addBatch(batch),
    onQueued: ({ batches, sync, lineItemIds, isPartial }) => {
      isPartialRunRef.current = Boolean(isPartial);

      if (batches.length > 1 || isPartial) {
        /* One operator action, N server-side runs, and nothing on the wire links them — no
         * shared request id BY DESIGN, it would collide the label-regen FIFO message groups.
         * This line is what lets support stitch the action back together. */
        // eslint-disable-next-line no-console
        console.info('[bulk-override] chunked commit', {
          operation,
          batchIds: batches.map((batch) => batch.batchId),
          isPartial: Boolean(isPartial),
        });
      }

      /* A chunked run's tracker is live and its batches already registered; this is the moment
       * it learns no more are coming. */
      if (progressRunBegunRef.current) {
        progress.finishRun({ sync });

        return;
      }

      /* Single-request run: everything is known at once, so tracking starts here. */
      const isTrackingLabels = startLabelTracking(lineItemIds) || labelTrackingStartedRef.current;

      progress.startTracking({
        batches,
        sync,
        ...buildOverrideRunCallbacks({ isTrackingLabels }),
      });
    },
  });
  const { isReview, isProductChange, isUnfulfillment, isQuantityChange, product, targetProduct } =
    bo;

  // Assigned after construction so the queued-run callbacks above can reach it without closing
  // over `bo` while it is still being initialised.
  reloadRowsRef.current = bo.retry;
  settleRef.current = bo.settleAfterCommit;

  /* Closing unmounts this dialog, so staged values, the selection and the filters all go
   * with it. Back is navigation inside the flow and stays silent; leaving does not. */
  const requestClose = () => {
    if (bo.stagedIds.length) {
      setIsDiscardOpen(true);
      return;
    }

    onClose();
  };

  const withFacilityName = useCallback(
    (row) => ({
      ...row,
      facilityName:
        find(facilities, { fulfillingPharmacyId: row.fulfillingPharmacyId })?.name ??
        row.fulfillingPharmacyId,
    }),
    [facilities]
  );

  const rows = useMemo(
    () => bo.visibleRows.map(withFacilityName),
    [bo.visibleRows, withFacilityName]
  );

  /* AG Grid owns the displayed order once a column is sorted, and rows never reorders, so a
   * capped select-all has to ask the grid which rows are actually on top. */
  const orderedVisibleIds = useCallback(() => {
    const api = gridRef.current?.api;

    if (!api) return rows.map((row) => row.lineItemId);

    const ids = [];

    api.forEachNodeAfterFilterAndSort(({ data }) => {
      if (data) ids.push(data.lineItemId);
    });

    return ids;
  }, [rows]);

  /* Deduped by name: the filter compares on partnerName, so two partners sharing a name are one
   * choice to the operator and to visibleRows. Listing both also collided on the React key. */
  /* Sorted with localeCompare, not the default comparator: default sorts by UTF-16 code unit, so
   * an upper-case letter beats every lower-case one and "PerfectRx" would come before
   * "PerfectionRx". localeCompare collates on the base letter (i before r), which is the
   * alphabetical order a reader expects (POM-6137 QA). */
  const partnerOptions = useMemo(
    () =>
      uniqBy(
        map(partners, ({ partnerName, partnerUuid }) => ({
          /* value stays exactly as listPartners returned it -- the filter matches it against
           * row.partner, so normalising it here would stop the filter finding its own rows. Only
           * the LABEL is capitalised, which is also what the sort below then collates on, so a
           * lower-case first letter no longer lands the partner away from its own letter. */
          value: partnerName,
          label: upperFirst(partnerName),
          key: partnerUuid,
        })),
        'value'
      ).sort((first, second) => (first.label || '').localeCompare(second.label || '')),
    [partners]
  );

  /* Same collation as partnerOptions -- see the note there. */
  const facilityOptions = useMemo(
    () =>
      map(facilities, ({ fulfillingPharmacyId, name }) => ({
        value: fulfillingPharmacyId,
        label: name,
      })).sort((first, second) => (first.label || '').localeCompare(second.label || '')),
    [facilities]
  );

  const hasScope = Boolean(product);
  const ndcFilterOptions = useMemo(
    () => bo.ndcOptions.map(({ ndc }) => ({ value: ndc, label: ndc })),
    [bo.ndcOptions]
  );

  /* The target's NDCs, for the New NDC picker and the row selects. */
  const targetNdcOptions = useMemo(
    () => bo.targetNdcOptions.map(({ ndc }) => ({ value: ndc, label: ndc })),
    [bo.targetNdcOptions]
  );

  /* Flagged, not filtered out: the operator asked for this NDC in the filter above, so removing
   * it from the list reads as a bug. It stays listed and unselectable. Only in NDC update -- in a
   * product change the same NDC on a different product is a real change, and core only skips a
   * row when product, NDC and quantity all already match. */
  const noOpNdc =
    isReview || isProductChange || isUnfulfillment ? '' : soleCurrentNdc(bo.visibleRows);
  const updateNdcOptions = useMemo(
    () =>
      ndcFilterOptions.map((option) =>
        noOpNdc && ndcDigits(option.value) === noOpNdc ? { ...option, isNoOp: true } : option
      ),
    [ndcFilterOptions, noOpNdc]
  );

  /* Two different drugs in a product change: the filter above describes the orders as they are,
   * this picker offers what they are moving to. Every other mode has one drug, so both are it. */
  const pickerOptions = () => {
    if (isUnfulfillment) return [];
    if (isReview) return reasonOptions;

    return isProductChange ? targetNdcOptions : updateNdcOptions;
  };

  const valueOptions = pickerOptions();
  const valueLabel = isReview ? 'Review reason' : 'New NDC';
  const placeholder = isReview ? 'Select a reason…' : 'Select NDC…';
  const rowPlaceholder = isReview ? 'Select reason…' : 'Select NDC…';
  const isValuePickerReady = !isProductChange || Boolean(targetProduct);

  const selectedRows = rows.filter((row) => bo.selectedIds.includes(row.lineItemId));
  const selectedNdcs = [...new Set(selectedRows.map((row) => row.ndc))];
  const changedCount = rows.filter(bo.isRowChanged).length;

  /* Clearing the quantity filter must not hide a staged quantity: the Qty column strikes the old
   * value through, and with New qty gone the operator would see a struck-through number with no
   * replacement anywhere while the commit count still counted it. Rebuild risk is nil -- while the
   * filter is set this is already true, so the OR only changes state at the moment the filter is
   * cleared, never while a cell is being typed into. */
  const hasStagedQuantity = useMemo(
    () =>
      bo.rows.some((row) =>
        isStagedQuantity((bo.stagedValues[row.lineItemId] || EMPTY_ROW_EDIT).quantity)
      ),
    [bo.rows, bo.stagedValues]
  );

  /* Keyed by a STRING, never by the staged value itself. Review stages a scalar reason, but a
   * quantity update stages a {ndc, quantity} object and every row gets its own instance -- so
   * bucketing on the value gave one entry per row, all rendering under the key "[object Object]",
   * which React reports as duplicate children. Quantity buckets on the number instead. */
  const stagedBuckets = useMemo(() => {
    const counts = new Map();
    rows.forEach((row) => {
      if (!bo.isRowChanged(row)) return;

      const staged = bo.stagedValues[row.lineItemId];
      const key = stagedBucketKey({
        staged,
        isQuantityChange,
        isFillLaterSplit,
        isFillLaterMove,
        isProductChange,
      });

      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return [...counts];
  }, [rows, bo, isQuantityChange, isFillLaterSplit, isFillLaterMove, isProductChange]);

  const modalClass = ['modal', hasScope ? 'hasdrug' : 'nodrug', bo.isRowsLoading ? 'busy' : '']
    .filter(Boolean)
    .join(' ');

  /* The visible rows, not every fetched one: a filtered-out row is not committed, so counting it
   * in the summary would describe a write that is not going to happen. */
  const stagedRows = rows.filter(bo.isRowChanged).map(withFacilityName);
  const untouchedSelected = rows.filter(
    (row) => bo.selectedIds.includes(row.lineItemId) && !bo.isRowChanged(row)
  ).length;

  /* Only what the operation itself is about. Facilities, partners and a unit total were
   * describing the selection rather than the change, and the operator had just made that
   * selection on the screen behind. */
  const confirmScope = () => {
    const target = isProductChange
      ? [
          { label: 'Moving from', value: product?.displayName },
          { label: 'Moving to', value: targetProduct?.displayName },
        ]
      : [isReview ? null : { label: `Drug`, value: product?.displayName }];

    /* The per-order lines below say what happens to ONE order. These say what the run does, and
       nothing else on the screen adds up to them: a schedule of three dates across 334 orders is
       1,002 fill laters and ten times the quantity any single row shows. That total is the part
       worth pausing over, so it is stated before the breakdown rather than left to be inferred. */
    if (isFillLaterMove) {
      const days = [
        ...new Set(
          stagedRows
            .map((row) => bo.stagedValues[row.lineItemId]?.scheduledFor)
            .filter(isRealCalendarDay)
        ),
      ].sort();

      return [
        ...target.filter(Boolean),
        { label: 'Items moving to fill later', value: `${stagedRows.length}` },
        days.length
          ? {
              label: days.length === 1 ? 'Fill date' : 'Fill dates',
              value:
                days.length === 1
                  ? formatScheduleDay(days[0])
                  : `${formatScheduleDay(days[0])} – ${formatScheduleDay(days[days.length - 1])}`,
            }
          : null,
      ].filter(Boolean);
    }

    if (isFillLaterSplit) {
      const schedules = stagedRows.map((row) => bo.stagedValues[row.lineItemId]?.splits);
      const fillLaterCount = schedules.reduce(
        (count, schedule) => count + completeLines(schedule).length,
        0
      );
      const quantityTotal = schedules.reduce(
        (total, schedule) => total + scheduleTotal(schedule),
        0
      );
      const days = [...new Set(schedules.flatMap((schedule) => scheduleDates(schedule)))].sort();
      const span = days.length
        ? [formatScheduleDay(days[0]), formatScheduleDay(days[days.length - 1])]
        : [];

      return [
        ...target.filter(Boolean),
        { label: 'Fill laters to create', value: `${fillLaterCount}` },
        { label: 'Quantity moving off these orders', value: `${quantityTotal}` },
        days.length
          ? {
              label: days.length === 1 ? 'Fill date' : 'Fill dates',
              value: span[0] === span[1] ? span[0] : `${span[0]} – ${span[1]}`,
            }
          : null,
      ].filter(Boolean);
    }

    return target.filter(Boolean);
  };

  const groupPairs = (pairs) => {
    const counts = new Map();

    pairs.forEach(({ from, to, orderId }) => {
      const key = `${from}-${to}`;
      const group = counts.get(key) ?? { from, to, count: 0, orderIds: [] };

      counts.set(key, {
        ...group,
        count: group.count + 1,
        orderIds: [...group.orderIds, orderId],
      });
    });

    return [...counts.values()].sort((first, second) => second.count - first.count);
  };

  const plural = (noun, length) => `${noun}${length === 1 ? '' : 's'}`;

  /* Grouped from every fetched row, not the visible ones. Stage three rows, filter one
   * away, and a breakdown built from the visible set would account for two while the
   * headline said three -- and the commit would still write all three. */
  const confirmSections = () => {
    if (isUnfulfillment) {
      const statusGroups = groupPairs(
        stagedRows.map((row) => ({
          from: 'Filled',
          to: 'Awaiting product fulfillment',
          orderId: row.orderId,
        }))
      );
      return [{ label: 'Status change', groups: statusGroups }];
    }

    /* Product change carries two changes at once and the scope lines above name only the drugs,
     * so each gets its own breakdown. The NDC is the point of the operation -- an operator
     * confirming 65 orders has to see which NDC they are getting, not only that the product
     * moves. A value that is unchanged is left out of that value's list. */
    if (isProductChange) {
      const ndcPairs = [];
      const quantityPairs = [];

      stagedRows.forEach((row) => {
        const edit = bo.stagedValues[row.lineItemId] || EMPTY_ROW_EDIT;
        const ndc = edit.ndc || row.ndc;

        if (ndc && ndc !== row.ndc) {
          ndcPairs.push({ from: row.ndc, to: ndc, orderId: row.orderId });
        }

        if (isStagedQuantity(edit.quantity) && Number(edit.quantity) !== Number(row.quantity)) {
          quantityPairs.push({
            from: String(row.quantity),
            to: String(edit.quantity),
            orderId: row.orderId,
          });
        }
      });

      const ndcGroups = groupPairs(ndcPairs).map((group) => ({ ...group, isMono: true }));
      const quantityGroups = groupPairs(quantityPairs);

      return [
        { label: plural('NDC change', ndcGroups.length), groups: ndcGroups },
        { label: plural('Quantity change', quantityGroups.length), groups: quantityGroups },
      ].filter((section) => section.groups.length > 0);
    }

    /* Its own branch for the same reason the product change has one: the fall-through below reads
     * the staged value as the scalar it is in NDC and review mode, but a quantity update stages a
     * {ndc, quantity} object -- handing that straight to ChangeLine rendered an object as a React
     * child and took the whole dialog down behind the error boundary. */
    if (isQuantityChange) {
      const quantityGroups = groupPairs(
        stagedRows.map((row) => ({
          from: String(row.quantity),
          to: String((bo.stagedValues[row.lineItemId] || EMPTY_ROW_EDIT).quantity),
          orderId: row.orderId,
        }))
      );

      return [{ label: plural('Quantity change', quantityGroups.length), groups: quantityGroups }];
    }

    if (isFillLaterMove) {
      const moveGroups = groupPairs(
        stagedRows.map((row) => ({
          from: null,
          to: formatScheduleDay(bo.stagedValues[row.lineItemId]?.scheduledFor),
          orderId: row.orderId,
        }))
      );

      return [{ label: plural('Fill later move', moveGroups.length), groups: moveGroups }];
    }

    if (isFillLaterSplit) {
      /* One entry per SPLIT, not one per row. Grouping on (quantity, date) collapses the common
         case -- one schedule stamped across the selection -- to a line per split, and still
         separates rows that were adjusted on their own. What each original keeps is not restated
         here: the grid's Remaining quantity column already carries it row by row. */
      const splitPairs = [];

      stagedRows.forEach((row) => {
        const schedule = bo.stagedValues[row.lineItemId]?.splits;

        completeLines(schedule).forEach((line) => {
          splitPairs.push({
            quantity: Number(line.quantity),
            scheduledFor: line.scheduledFor,
            orderId: row.orderId,
          });
        });
      });

      /* Grouped here rather than through groupPairs: that helper keeps only from/to/count, and
         this section needs the quantity itself to state what the run actually moves on each date
         -- "334 orders · 3,340 units" is the number worth pausing over, and it is not derivable
         from anything else on the screen. Sorted by date, because a schedule reads in date order
         and groupPairs sorts by count. */
      const bySplit = new Map();

      splitPairs.forEach((pair) => {
        const key = `${pair.scheduledFor}|${pair.quantity}`;
        const seen = bySplit.get(key) ?? { ...pair, count: 0 };

        bySplit.set(key, { ...seen, count: seen.count + 1 });
      });

      const splitGroups = [...bySplit.values()]
        .sort(
          (first, second) =>
            first.scheduledFor.localeCompare(second.scheduledFor) ||
            first.quantity - second.quantity
        )
        .map((group) => {
          const away = relativeScheduleDay(group.scheduledFor);

          return {
            from: null,
            to: `${group.quantity} QTY on ${formatScheduleDay(group.scheduledFor)}${
              away ? ` · ${away}` : ''
            }`,
            count: group.count,
            note: `${(group.quantity * group.count).toLocaleString()} units`,
          };
        });

      return [
        { label: plural('Fill later split', splitGroups.length), groups: splitGroups },
      ].filter((section) => section.groups.length > 0);
    }

    const groups = groupPairs(
      stagedRows.map((row) => ({
        from: isReview ? null : row.ndc,
        to: bo.stagedValues[row.lineItemId],
        orderId: row.orderId,
      }))
    ).map((group) => ({
      ...group,
      to: isReview ? BULK_OVERRIDE_REVIEW_REASONS[group.to] : group.to,
      isMono: !isReview,
    }));

    return [{ label: plural(isReview ? 'Review reason' : 'NDC change', groups.length), groups }];
  };

  const changeSections = confirmSections();

  return (
    <>
      <BulkOverridePortal>
        <div className='veil up'>
          <div
            className={modalClass}
            data-mode={MODE[operation]}
            role='dialog'
            aria-modal='true'
            aria-label={action.title}
          >
            <BulkOverrideHeader
              actionTitle={action.title}
              stepCount={isFillLaterSplit || isFillLaterMove ? 3 : 2}
              title={
                hasScope
                  ? (product?.displayName ?? 'Send orders to review')
                  : 'Select a drug to begin'
              }
              productId={product?.productId}
              lockLabel={BULK_OVERRIDE_STANDING_FILTER_LABEL[copyKey]}
              onBack={onBack}
              onReset={bo.resetStaging}
              onClose={requestClose}
            />

            <BulkOverrideFilterRow
              isReview={isReview}
              product={product}
              drugOptions={bo.drugOptions}
              isDrugLoading={bo.isDrugLoading}
              filters={bo.filters}
              facilityOptions={facilityOptions}
              partnerOptions={partnerOptions}
              ndcOptions={ndcFilterOptions}
              onDrugInput={(value) => {
                if (value.length >= MIN_DRUG_SEARCH_LENGTH) bo.searchDrugs(value);
              }}
              onDrugChange={bo.handleProduct}
              onFilterChange={(name, value) => {
                if (name === 'dateRange') {
                  bo.changeFilter('from', value.from);
                  bo.changeFilter('to', value.to);
                  return;
                }
                bo.changeFilter(name, value);
              }}
            />

            {!isUnfulfillment && (
              <BulkOverrideApplyBar
                isFillLaterSplit={isFillLaterSplit}
                isFillLaterMove={isFillLaterMove}
                moveDate={bo.moveDate}
                moveDateIssue={bo.moveDateIssue}
                onMoveDateChange={bo.changeMoveDate}
                schedule={bo.schedule}
                scheduleIssue={bo.scheduleIssue}
                onScheduleLineChange={bo.changeScheduleLine}
                onScheduleLineAdd={bo.addScheduleLine}
                onScheduleLineRemove={bo.removeScheduleLine}
                note={BULK_OVERRIDE_APPLY_NOTE[copyKey]}
                isReview={isReview}
                isProductChange={isProductChange}
                isUnfulfillment={isUnfulfillment}
                isQuantityChange={isQuantityChange}
                /* What Apply will touch, not what is ticked: a selection made before a filter
                   was narrowed still holds rows that are now off screen, and this bar's number
                   is a promise about the button beside it. */
                selectedCount={bo.visibleSelectedIds.length}
                selectedNdcs={selectedNdcs}
                valueLabel={valueLabel}
                valueOptions={valueOptions}
                placeholder={placeholder}
                value={bo.bulkValue}
                isValueRequired={isReview}
                isValuePickerReady={isValuePickerReady}
                targetProduct={targetProduct}
                drugOptions={bo.drugOptions.filter(
                  ({ productId }) => productId !== product?.productId
                )}
                isDrugLoading={bo.isDrugLoading}
                onTargetProductChange={bo.handleTargetProduct}
                onDrugInput={(value) => {
                  if (value.length >= MIN_DRUG_SEARCH_LENGTH) bo.searchDrugs(value);
                }}
                onValueChange={bo.setBulkValue}
                onApply={bo.applyToSelection}
                showQuantity={
                  isQuantityChange ||
                  isFillLaterSplit ||
                  (isProductChange && isValuePickerReady && bo.isQtyFilterActive)
                }
                /* Quantity mode asks for the filter FIRST: it makes every row on screen share one
                   current quantity, so one new value is either valid for the whole selection or
                   for none of it, and the before/after is a single pair rather than 65 different
                   ones. Product change already gated its own quantity column this way. */
                isQuantityGateUnmet={
                  (isQuantityChange || isFillLaterSplit) && !bo.isQtyFilterActive
                }
                quantityValue={bo.bulkQuantity}
                filteredQuantity={bo.qtyFilterValue}
                onQuantityChange={bo.setBulkQuantity}
              />
            )}

            {!isUnfulfillment && !isFillLaterMove && (
              <div className='buckets'>
                <span className='bk-l'>Staged</span>
                {stagedBuckets.length ? (
                  stagedBuckets.map(([reason, count]) => (
                    <span className='bk' key={reason}>
                      {stagedBucketLabel({
                        reason,
                        isQuantityChange,
                        isFillLaterSplit,
                        isFillLaterMove,
                        isProductChange,
                      })}
                      <b>{count}</b>
                    </span>
                  ))
                ) : (
                  <span className='bk none'>Nothing staged yet</span>
                )}
                <span className='bk rest'>{`${rows.length - changedCount} untouched`}</span>
              </div>
            )}

            <BulkOverrideTable
              rows={rows}
              isReview={isReview}
              isLoading={bo.isRowsLoading}
              hasScope={hasScope}
              hasRowsBeforeFilters={bo.rows.length > 0}
              loadError={Boolean(bo.loadError)}
              onRetry={bo.retry}
              valueColumnLabel={isProductChange ? 'New drug / NDC' : valueLabel}
              isProductChange={isProductChange}
              isUnfulfillment={isUnfulfillment}
              isFillLaterSplit={isFillLaterSplit}
              isFillLaterMove={isFillLaterMove}
              isQuantityChange={isQuantityChange}
              targetProductName={targetProduct?.displayName}
              valueOptions={valueOptions}
              placeholder={rowPlaceholder}
              selectedIds={bo.selectedIds}
              stagedValues={bo.stagedValues}
              isRowChanged={bo.isRowChanged}
              isValuePickerReady={isValuePickerReady}
              gridRef={gridRef}
              onToggleRow={bo.toggleRow}
              onToggleAll={() => bo.toggleAll(orderedVisibleIds())}
              isQtyEditable={
                bo.isQtyFilterActive &&
                (isQuantityChange || (isProductChange && isValuePickerReady))
              }
              isQtyColumnVisible={isProductChange && (bo.isQtyFilterActive || hasStagedQuantity)}
              onRowValueChange={bo.changeRowValue}
              onRowFieldChange={bo.changeRowField}
              onClearFilters={() => bo.handleProduct(product)}
              onChangeDrug={() => bo.handleProduct(null)}
            />

            <BulkOverrideFooter
              /* Blocked while the schedule itself is unsound, or while every staged row is one
                 the server would refuse -- committing then would report a run of failures the
                 operator could have seen on the rows first. */
              isCommitBlocked={
                (isFillLaterMove && Boolean(bo.moveDateIssue)) ||
                (isFillLaterSplit &&
                  (Boolean(bo.scheduleIssue) || bo.scheduleBreakdown.ready.length === 0))
              }
              hasScope={hasScope}
              isLoading={bo.isRowsLoading}
              rowCount={rows.length}
              selectedCount={bo.selectedIds.length}
              stagedCount={bo.stagedIds.length}
              hiddenStagedCount={bo.hiddenStagedCount}
              /* Also locked while EITHER tracker of a previous run is alive: a partial run
               * closes the dialog with rows still staged, and a second commit then would clear
               * the live run's partial flag, hijack its label wait and cross-wire the two runs'
               * counts. The retry unlocks when the accepted batches settle. */
              isCommitting={bo.isCommitting || progress.isTracking || labelProgress.isTracking}
              selectLimit={bo.selectLimit}
              selectTargetCount={bo.limitIds(rows).length}
              onSelectLimitChange={bo.setSelectLimit}
              onSelectAll={() => bo.setSelectedIds(bo.limitIds(orderedVisibleIds()))}
              onClearSelection={bo.clearSelection}
              onBack={onBack}
              onCommit={() => setIsConfirmOpen(true)}
            />
          </div>
        </div>
      </BulkOverridePortal>

      {isConfirmOpen && (
        <BulkOverrideConfirmDialog
          title={CONFIRM_TITLE[copyKey]}
          count={bo.stagedIds.length}
          scope={confirmScope()}
          sections={changeSections}
          untouchedCount={untouchedSelected}
          selectedCount={bo.selectedIds.length}
          note={CONFIRM_NOTE[copyKey]}
          isCommitting={bo.isCommitting}
          progress={progress}
          labelProgress={hasLabelPhase ? labelProgress : null}
          ndcOutcome={ndcOutcome}
          stepOneLabel={STEP_ONE_LABEL[copyKey]}
          workingSubtitle={WORKING_SUBTITLE[copyKey]}
          onClose={() => setIsConfirmOpen(false)}
          onConfirm={async () => {
            const outcome = await bo.commit();

            /* A chunked commit arms both trackers before its first submit. If the run ended up
             * not queueing anything — every chunk ran synchronously, or nothing was accepted —
             * they are waiting on events that will never be scoped to a batch, so disarm them
             * rather than letting a 15-minute timeout own the dialog. */
            const armedEarly = labelTrackingStartedRef.current || progressRunBegunRef.current;

            if (!outcome?.queued && armedEarly) {
              labelTrackingStartedRef.current = false;
              progressRunBegunRef.current = false;
              labelsSettledRef.current = true;
              labelProgress.reset();
              progress.reset();
            }

            /* Some chunks were accepted, then a submit failed. What was accepted is running (its
             * progress tracker is live if anything queued); what was not stays staged and
             * selected as the retry record. HOW it failed decides the advice: a definite
             * rejection is safe to retry, an indeterminate one may already be processing. */
            if (outcome?.partial) {
              setIsConfirmOpen(false);

              /* No queued settle will refresh the grid, and synchronous chunks may have written
               * rows before the failure — left stale they look untouched and invite a duplicate
               * commit. */
              if (!outcome.queued) bo.retry();

              const planned = outcome.submittedCount + outcome.unsubmittedCount;

              enqueueSnackbar(
                outcome.isDefinite
                  ? `Submitted ${outcome.submittedCount} of ${planned} orders. ` +
                      `${outcome.unsubmittedCount} were not submitted — they are still staged; ` +
                      'review and retry.'
                  : `Submitted ${outcome.submittedCount} of ${planned} orders. The failed request ` +
                      `may still be processing — the remaining ${outcome.unsubmittedCount} stay ` +
                      'staged; give it a minute and re-check this list before retrying.',
                { variant: 'warning', autoHideDuration: 15000 }
              );

              return;
            }

            /* A queued run keeps the dialog open so its progress bar has somewhere to live —
             * onQueued has already started the tracker, and the tracker's own callbacks close it.
             * Everything else settles here as before. */
            if (outcome?.queued) return;

            setIsConfirmOpen(false);

            if (!outcome) return;

            if (outcome.failedCount) {
              enqueueSnackbar(
                `${outcome.failedCount} of ${outcome.total} orders could not be updated.`,
                { variant: 'warning', autoHideDuration: 10000 }
              );

              return;
            }

            enqueueSnackbar(
              `${outcome.total} order${outcome.total === 1 ? '' : 's'} ${COMMITTED_VERB[copyKey]}.`,
              { variant: 'success' }
            );
          }}
        />
      )}

      {isDiscardOpen && (
        <BulkOverrideDiscardDialog
          count={bo.stagedIds.length}
          onCancel={() => setIsDiscardOpen(false)}
          onDiscard={() => {
            setIsDiscardOpen(false);
            onClose();
          }}
        />
      )}
    </>
  );
};

BulkOverrideDialog.propTypes = {
  operation: PropTypes.string.isRequired,
  fillLaterMode: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

/* ---------- BulkOverrideButton : the toolbar entry point ---------- */

const BulkOverrideButton = () => {
  const [isChooserOpen, setIsChooserOpen] = useState(false);
  const [isFillLaterModeOpen, setIsFillLaterModeOpen] = useState(false);
  const [fillLaterMode, setFillLaterMode] = useState(FILL_LATER_MODE.SPLIT_QUANTITY);
  const [operation, setOperation] = useState(null);
  const { bulkUpdateOrders } = useRoles();
  const { labelItemIds } = usePrintLabelContext();

  // Bulk Update runs its own selection on a full-screen step, so a selection
  // already made in the queue behind it would be silently ignored.
  const hasQueueSelection = !isEmpty(labelItemIds);

  /* The chooser stays MOUNTED behind the mode screen rather than being swapped out. Closing it
   * first dropped the operator back onto the bare work queue for the length of one decision,
   * which read as having lost the flow. Its veil dims what is behind it, so the action they just
   * picked stays on screen and recedes. */
  const handleContinue = (chosenOperation) => {
    if (chosenOperation === bulkOverrideOperation.FILL_LATER_SPLIT) {
      setIsFillLaterModeOpen(true);
      return;
    }

    setOperation(chosenOperation);
    setIsChooserOpen(false);
  };

  const handleFillLaterMode = (chosenMode) => {
    setFillLaterMode(chosenMode);
    setOperation(bulkOverrideOperation.FILL_LATER_SPLIT);
    setIsFillLaterModeOpen(false);
    setIsChooserOpen(false);
  };

  const handleFillLaterModeBack = () => setIsFillLaterModeOpen(false);

  if (!bulkUpdateOrders) return null;

  return (
    <>
      <Tooltip title={hasQueueSelection ? 'Clear your selection to start a bulk update' : ''}>
        <span>
          <Button
            variant='contained'
            color='primary'
            disabled={hasQueueSelection}
            onClick={() => setIsChooserOpen(true)}
            sx={{ height: 33 }}
          >
            Bulk Update
          </Button>
        </span>
      </Tooltip>

      {isChooserOpen && (
        <BulkOverrideChooser onClose={() => setIsChooserOpen(false)} onContinue={handleContinue} />
      )}

      {isFillLaterModeOpen && (
        <BulkFillLaterModeChooser
          onBack={handleFillLaterModeBack}
          onContinue={handleFillLaterMode}
        />
      )}

      {operation && (
        <BulkOverrideDialog
          operation={operation}
          fillLaterMode={fillLaterMode}
          onClose={() => setOperation(null)}
          onBack={() => {
            setOperation(null);
            setIsChooserOpen(true);
          }}
        />
      )}
    </>
  );
};

export default BulkOverrideButton;
